/**
 * WebGLMarkerLayer — High-performance WebGL-based marker renderer
 * for 10,000+ fleet vehicle markers using deck.gl.
 *
 * Replaces google.maps.Marker + MarkerClusterer for bulk unit rendering.
 * Non-unit markers (landmarks, routes, pointers) still use MarkerCompat.
 *
 * Performance characteristics:
 *   - All markers in a single WebGL draw call
 *   - GPU-accelerated icon rotation (heading)
 *   - Native viewport culling (no manual bounds checking)
 *   - Efficient diffing on data updates (only changed markers re-render)
 *   - Supercluster-based clustering computed in JS, rendered via WebGL
 *   - Labels rendered via deck.gl TextLayer (pure WebGL, 0 DOM nodes, instant)
 *
 * Usage in googleProvider.js:
 *   const webgl = new WebGLMarkerLayer(mapInstance);
 *   webgl.updateMarkers(unitMapObjects);
 *   webgl.setOnClick(handler);
 *   webgl.enableClustering({ maxZoom: 18, radius: 200 });
 *   webgl.setZoom(mapInstance.getZoom());
 *   webgl.destroy();
 */

import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { IconLayer, PathLayer, TextLayer } from '@deck.gl/layers';
import Supercluster from 'supercluster';

import { EventColorMap, ColorsToRoutePointsOnRoutesTracker } from './enums';

/* ─────────── Event Color Lookup (fast O(1) Map) ─────────── */
const _eventColorMap = new Map();
EventColorMap.forEach(({ eventName, color }) => {
  _eventColorMap.set(eventName, color);
});

/**
 * Resolve the marker color from its event name.
 * Falls back to the default gray (#d7d7d6) when the event is unknown.
 */
const resolveEventColor = (eventName) => {
  if (!eventName) return '#d7d7d6';
  return _eventColorMap.get(eventName) || '#d7d7d6';
};

/**
 * Collect all unique event colors (including default gray) used by arrows.
 * Pre-warming the arrow URL cache + loading the Image objects at module init
 * ensures the icon atlas never misses arrows during the first render.
 */
const _allArrowColors = new Set(EventColorMap.map(e => e.color));
_allArrowColors.add('#d7d7d6'); // default gray

/**
 * Maximum number of labels rendered in a single TextLayer draw call.
 * Prevents WebGL vertex buffer overflow (GL_INVALID_OPERATION:
 * glDrawArraysInstanced) on accounts with 3000+ units where the
 * collision filter fallback would otherwise pass all labels through.
 * 500 labels × ~20 chars ≈ 10,000 glyph quads — well within GPU limits.
 */
const MAX_VISIBLE_LABELS = 500;

/**
 * Shared SDF font settings for all TextLayers.
 *
 * Tuned to avoid WebGL vertex buffer overflow (GL_INVALID_OPERATION:
 * glDrawArraysInstanced) on large accounts (3000+ units).
 *
 * With fontSize: 256 + buffer: 32, each glyph cell is ~320px and the
 * atlas canvas (fixed 1024px wide) grows to ≈16,384px tall — exceeding
 * MAX_TEXTURE_SIZE on many GPUs.  Reducing to fontSize: 64 + buffer: 8
 * keeps the exact same fontSize/8 ratio for thin-stroke SDF coverage
 * while shrinking the atlas to ≈2,048px — well within GPU limits.
 *
 * At getSize 12px display, 64px atlas resolution is 5× — more than
 * sufficient for crisp zoom-independent text via SDF.
 */
const LABEL_FONT_SETTINGS = {
  sdf: true,
  fontSize: 256,
  buffer: 32,
  cutoff: 0.15,
};

/* ─────────── Icon Mapping ─────────── */

/**
 * Arrow SVG path data (shared by arrow and video arrow generators).
 * The arrow points North (up) — deck.gl rotates it via getAngle.
 */
const ARROW_PATH = 'M 12.11,2.48 C 12.11,2.48 4.61,20.77 4.61,20.77 4.61,20.77 5.32,21.48 5.32,21.48 5.32,21.48 12.11,18.48 12.11,18.48 12.11,18.48 18.90,21.48 18.90,21.48 18.90,21.48 19.61,20.77 19.61,20.77 19.61,20.77 12.11,2.48 12.11,2.48 Z';

/**
 * Default SVG arrow icon used for most vehicle markers.
 * Encoded as data URI to avoid network requests.
 * Features a subtle white outline for visibility on any map background.
 */
const ARROW_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 24 24">
  <path d="${ARROW_PATH}" fill="white" fill-opacity="1" stroke="white" stroke-width="0.8"/>
</svg>`;

const ARROW_ICON_URL = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(ARROW_SVG);

/**
 * Create a colored arrow SVG as a data URI.
 * Memoized by color to avoid repeated SVG string creation + URI encoding.
 * With 3000 markers and ~20 distinct colors this cache stays small.
 */
const _arrowUrlCache = new Map();
const createColoredArrowUrl = (color) => {
  if (_arrowUrlCache.has(color)) return _arrowUrlCache.get(color);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 24 24">
    <path d="${ARROW_PATH}"
      fill="${color}" fill-opacity="1" stroke="white" stroke-width="0.8"/>
  </svg>`;
  const url = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
  _arrowUrlCache.set(color, url);
  return url;
};

/**
 * Create a colored arrow with a video indicator circle in the center.
 * The circle is a white-filled dot that distinguishes camera-equipped units.
 * Memoized by color.
 */
const _videoArrowUrlCache = new Map();
const createColoredVideoArrowUrl = (color) => {
  if (_videoArrowUrlCache.has(color)) return _videoArrowUrlCache.get(color);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 24 24">
    <path d="${ARROW_PATH}"
      fill="${color}" fill-opacity="1" stroke="white" stroke-width="0.8"/>
    <circle cx="12.11" cy="13.5" r="2.2" fill="white" opacity="0.95"/>
  </svg>`;
  const url = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
  _videoArrowUrlCache.set(color, url);
  return url;
};

// Pre-warm arrow URL caches for ALL known event colors at module load.
// This ensures the data URI strings are ready before the first render,
// eliminating any chance that a color arrow is missing from the icon atlas
// due to a cache-miss race during the initial IconLayer creation.
_allArrowColors.forEach(color => {
  createColoredArrowUrl(color);
  createColoredVideoArrowUrl(color);
});

/**
 * Pre-load arrow Images so deck.gl's icon atlas already has them decoded
 * when the first render occurs.  Data URI images decode nearly instantly,
 * but queuing them eagerly avoids any edge-case async gap between the
 * first `getIcon` call and the atlas pack step.
 */
const _preloadedImages = new Map();
_allArrowColors.forEach(color => {
  const img = new Image(128, 128);
  img.src = createColoredArrowUrl(color);
  _preloadedImages.set(color, img);
  const imgV = new Image(128, 128);
  imgV.src = createColoredVideoArrowUrl(color);
  _preloadedImages.set('v_' + color, imgV);
});

/**
 * Offline marker icon (png-based, no rotation needed).
 */
const OFFLINE_ICON_URL = '/assets/icons/offlineimage.png';

/* ─────────── Route Stop Icon Generation ─────────── */

/**
 * Fast O(1) lookup for route tracker stop status → color/textColor.
 * Built once at module load from ColorsToRoutePointsOnRoutesTracker.
 */
const _routeStopStatusColors = new Map();
ColorsToRoutePointsOnRoutesTracker.forEach(({ name, color }) => {
  _routeStopStatusColors.set(name, color);
});

/**
 * SVG pin path for route stop markers — matches the legacy
 * IconStringMarkerRoutePoint template from Map/utils.js.
 */
const ROUTE_STOP_PIN_PATH = 'M16 0C7.164 0 0 7.164 0 16c0 14.592 16 32 16 32s16-17.408 16-32C32 7.164 24.836 0 16 0zm0 22c-2.208 0-4-1.792-4-4s1.792-4 4-4s4 1.792 4 4s-1.792 4-4 4z';

/**
 * Create a route-stop pin SVG as a data URI.
 *
 * Renders the classic filled-balloon pin at 512×512 (4× the original 128)
 * so deck.gl's icon atlas rasterises it with crisp edges.  The viewBox
 * is expanded to "-4 0 40 48" to accommodate the circle (cx=16, r=20)
 * that slightly overflows the original 32-wide viewport.
 *
 * Memoized by (color + number + textColor).
 *
 * @param {string} color       Fill color for the pin
 * @param {number} number      Stop sequence number (0 = no number shown)
 * @param {string} textColor   Color for the number text
 * @returns {string}           data:image/svg+xml URI
 */
const _routeStopIconCache = new Map();
const createRouteStopIconUrl = (color, number, textColor = 'white') => {
  const key = `${color}-${number}-${textColor}`;
  if (_routeStopIconCache.has(key)) return _routeStopIconCache.get(key);
  const numberText = number > 0 ? number : '';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="512" height="512" viewBox="-4 0 40 48">`
    + `<path d="${ROUTE_STOP_PIN_PATH}" fill="${color}"/>`
    + `<circle cx="16" cy="20" r="20" fill="${color}"/>`
    + `<text x="16" y="18" text-anchor="middle" fill="${textColor}" font-size="18" font-weight="bold" font-family="Arial,sans-serif" dy=".4em">${numberText}</text>`
    + `</svg>`;
  const url = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
  _routeStopIconCache.set(key, url);
  return url;
};

/** TypeLayer.FSMRoute numeric value — avoids importing the full enum */
const LAYER_FSM_ROUTE = 20;

/* ─────────── Cluster Icon Generation ─────────── */

/** Color thresholds for cluster icons (matches legacy: blue < 20, yellow 20-99, red 100+) */
const getClusterColor = (count) => {
  if (count >= 100) return '#ff0000';
  if (count >= 20) return '#e1ad01';
  return '#1dafed';
};

/**
 * Cluster icon matching the original map_cluster.svg design:
 * solid center circle + broken outer ring segments.
 *
 * The count text is rendered via a separate TextLayer to keep the icon
 * atlas small (only 3 entries) and avoid texture packing issues.
 *
 * Memoized by color.
 */
const _clusterIconCache = new Map();
const CLUSTER_SIZE = 60;

const createClusterIconUrl = (color) => {
  if (_clusterIconCache.has(color)) return _clusterIconCache.get(color);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${CLUSTER_SIZE}" height="${CLUSTER_SIZE}" viewBox="0 0 60 60">`
    + `<g>`
    + `<circle cx="30" cy="30" r="19.06" fill="${color}" opacity=".8"/>`
    + `<path fill="${color}" d="M28.68 56.92a27 27 0 0 1-25.6-25.53h3.1a23.86 23.86 0 0 0 22.5 22.43zm25.14-25.53a23.86 23.86 0 0 1-22.5 22.43v3.1a27 27 0 0 0 25.6-25.53zM28.68 6.18A23.86 23.86 0 0 0 6.17 28.76H3.08a27 27 0 0 1 25.6-25.68zm2.63-3.1v3.1a23.86 23.86 0 0 1 22.52 22.58h3.1A27 27 0 0 0 31.32 3.08z"/>`
    + `</g>`
    + `</svg>`;
  const url = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
  _clusterIconCache.set(color, url);
  return url;
};

/* ─────────── Pre-built Icon Atlases (Canvas 2D, fully synchronous) ─────────── */

/**
 * Build a Canvas-based icon atlas for arrow markers (normal + video variants).
 *
 * All arrow SVGs are rendered via Canvas 2D Path2D — fully synchronous, no
 * Image loading or URL decoding.  deck.gl receives the Canvas as `iconAtlas`
 * and a static `iconMapping` dict, so it never needs to rebuild its internal
 * texture atlas.  This eliminates the "disappearing icons on zoom/click" issue
 * caused by async atlas invalidation.
 *
 * Atlas layout (128×128 tiles, 10 per row):
 *   Rows 0..N:     Normal arrows (one per event color)
 *   Rows N+1..2N:  Video arrows  (arrow + white center dot)
 *
 * @returns {{ canvas: HTMLCanvasElement, mapping: Object<string, {x,y,width,height,anchorX,anchorY}> }}
 */
const ATLAS_TILE = 128;  // px per icon tile
const ATLAS_COLS = 10;   // tiles per row

function _buildArrowAtlas() {
  const colors = [..._allArrowColors];
  const rowsPerVariant = Math.ceil(colors.length / ATLAS_COLS);
  const totalRows = rowsPerVariant * 2; // normal + video

  const canvas = document.createElement('canvas');
  canvas.width = ATLAS_COLS * ATLAS_TILE;
  canvas.height = totalRows * ATLAS_TILE;
  const ctx = canvas.getContext('2d');

  const mapping = {};
  const scale = ATLAS_TILE / 24; // SVG viewBox is 0 0 24 24
  const arrowPath2D = new Path2D(ARROW_PATH);

  colors.forEach((color, i) => {
    const row = Math.floor(i / ATLAS_COLS);
    const col = i % ATLAS_COLS;
    const x = col * ATLAS_TILE;
    const yNormal = row * ATLAS_TILE;
    const yVideo = (rowsPerVariant + row) * ATLAS_TILE;

    // ── Normal arrow ──
    ctx.save();
    ctx.translate(x, yNormal);
    ctx.scale(scale, scale);
    ctx.fillStyle = color;
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 0.8;
    ctx.fill(arrowPath2D);
    ctx.stroke(arrowPath2D);
    ctx.restore();

    mapping[`arrow-${color}`] = {
      x, y: yNormal,
      width: ATLAS_TILE, height: ATLAS_TILE,
      anchorX: ATLAS_TILE / 2, anchorY: ATLAS_TILE / 2,
    };

    // ── Video arrow (arrow + white center dot) ──
    ctx.save();
    ctx.translate(x, yVideo);
    ctx.scale(scale, scale);
    ctx.fillStyle = color;
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 0.8;
    ctx.fill(arrowPath2D);
    ctx.stroke(arrowPath2D);
    // Video indicator circle
    ctx.beginPath();
    ctx.arc(12.11, 13.5, 2.2, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.fill();
    ctx.restore();

    mapping[`arrow-video-${color}`] = {
      x, y: yVideo,
      width: ATLAS_TILE, height: ATLAS_TILE,
      anchorX: ATLAS_TILE / 2, anchorY: ATLAS_TILE / 2,
    };
  });

  return { canvas, mapping };
}

/**
 * Build a Canvas-based icon atlas for cluster icons.
 * Contains 3 color variants (blue < 20, yellow 20-99, red 100+) matching
 * the legacy map_cluster.svg design: solid center circle + broken outer ring.
 *
 * Cluster count text is rendered by a separate TextLayer that shares the
 * EXACT same data array and coordinate accessor (`d => d.geometry.coordinates`),
 * so the number never drifts from the icon during rapid zoom.
 *
 * @returns {{ canvas: HTMLCanvasElement, mapping: Object<string, {x,y,width,height,anchorX,anchorY}> }}
 */
const CLUSTER_ATLAS_TILE = 128;

function _buildClusterAtlas() {
  const clusterCols = ['#1dafed', '#e1ad01', '#ff0000'];

  const canvas = document.createElement('canvas');
  canvas.width = clusterCols.length * CLUSTER_ATLAS_TILE;
  canvas.height = CLUSTER_ATLAS_TILE;
  const ctx = canvas.getContext('2d');

  const mapping = {};
  const scale = CLUSTER_ATLAS_TILE / 60; // SVG viewBox is 0 0 60 60
  const ringPath2D = new Path2D(
    'M28.68 56.92a27 27 0 0 1-25.6-25.53h3.1a23.86 23.86 0 0 0 22.5 22.43z'
    + 'm25.14-25.53a23.86 23.86 0 0 1-22.5 22.43v3.1a27 27 0 0 0 25.6-25.53z'
    + 'M28.68 6.18A23.86 23.86 0 0 0 6.17 28.76H3.08a27 27 0 0 1 25.6-25.68z'
    + 'm2.63-3.1v3.1a23.86 23.86 0 0 1 22.52 22.58h3.1A27 27 0 0 0 31.32 3.08z'
  );

  clusterCols.forEach((color, i) => {
    const x = i * CLUSTER_ATLAS_TILE;

    ctx.save();
    ctx.translate(x, 0);
    ctx.scale(scale, scale);

    // Center circle with 0.8 opacity
    ctx.globalAlpha = 0.8;
    ctx.beginPath();
    ctx.arc(30, 30, 19.06, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();

    // Outer ring segments (full opacity)
    ctx.globalAlpha = 1.0;
    ctx.fillStyle = color;
    ctx.fill(ringPath2D);

    ctx.restore();

    mapping[`cluster-${color}`] = {
      x, y: 0,
      width: CLUSTER_ATLAS_TILE, height: CLUSTER_ATLAS_TILE,
      anchorX: CLUSTER_ATLAS_TILE / 2, anchorY: CLUSTER_ATLAS_TILE / 2,
    };
  });

  return { canvas, mapping };
}

// Build atlases synchronously at module load — ready before any render call.
const { canvas: _arrowAtlasCanvas, mapping: _arrowAtlasMapping } = _buildArrowAtlas();
const { canvas: _clusterAtlasCanvas, mapping: _clusterAtlasMapping } = _buildClusterAtlas();

/**
 * Resolve an arrow icon atlas key, falling back to the default gray
 * if the exact color is not pre-rendered in the atlas.
 *
 * @param {string} color   Hex color of the arrow
 * @param {boolean} hasVideo  Whether the unit has a video indicator
 * @returns {string} A key present in _arrowAtlasMapping
 */
const _getArrowAtlasKey = (color, hasVideo) => {
  const prefix = hasVideo ? 'arrow-video-' : 'arrow-';
  const key = prefix + color;
  if (key in _arrowAtlasMapping) return key;
  // Fallback to default gray — always present in the atlas
  return prefix + '#d7d7d6';
};

/* ─────────── Data Conversion ─────────── */

/**
 * Convert a mapObject (from the internal mapObjects array) to deck.gl data format.
 *
 * @param {Object} mapObj  Internal MapObject with .id, .position, .heading,
 *                         .color, .icon, .eventName, .offline, .hasVideo, .enable, etc.
 * @returns {Object} { id, position, heading, color, iconUrl, visible, ... }
 */
const mapObjectToDeckData = (mapObj, existingUnit) => {
  const rawLat = typeof mapObj.position?.lat === 'function'
    ? mapObj.position.lat()
    : (mapObj.position?.lat ?? null);
  const rawLng = typeof mapObj.position?.lng === 'function'
    ? mapObj.position.lng()
    : (mapObj.position?.lng ?? null);

  const lat = rawLat != null ? (typeof rawLat === 'number' ? rawLat : parseFloat(rawLat)) : null;
  const lng = rawLng != null ? (typeof rawLng === 'number' ? rawLng : parseFloat(rawLng)) : null;

  // Determine icon URL
  let iconUrl = null;
  let useRotation = true;
  let isRouteStop = false;

  // ── Route Stop detection ──
  // Route stops (TypeLayer.FSMRoute = 20) use pin icons with status colors
  // and sequence numbers instead of the default vehicle arrow.
  if (mapObj.layerName === LAYER_FSM_ROUTE) {
    isRouteStop = true;
    useRotation = false;
  } else if (mapObj.offline) {
    iconUrl = OFFLINE_ICON_URL;
    useRotation = false;
  } else if (mapObj.icon && typeof mapObj.icon === 'string' && mapObj.icon.startsWith('/assets/') && !mapObj.icon.includes('marker/')) {
    // Custom static icon (not a directional marker)
    iconUrl = mapObj.icon;
    useRotation = false;
  }
  // else: use default arrow — color tint applied via getColor

  // ── Color + icon resolution ──
  let resolvedColor;

  // iconKey groups route stops by visual appearance (color + number + textColor)
  // so the icon atlas reuses a single texture entry for all stops that look the same.
  let iconKey = null;

  if (isRouteStop) {
    // Route stop color is derived from stop status, not vehicle event.
    if (mapObj.isRouteTracker) {
      const statusColor = _routeStopStatusColors.get(mapObj.eventName);
      resolvedColor = statusColor || '#d3d3d3';
      const textColor = mapObj.eventName === 'Pending' ? 'black' : 'white';
      const num = (mapObj.value ?? 0) + 1;
      iconUrl = createRouteStopIconUrl(resolvedColor, num, textColor);
      iconKey = `${resolvedColor}-${num}-${textColor}`;
    } else {
      // Template route: pink for stops, orange for waypoints
      if (mapObj.eventName === 'Waypoint') {
        resolvedColor = '#ff6623';
        iconUrl = createRouteStopIconUrl(resolvedColor, 0);
        iconKey = `${resolvedColor}-0-white`;
      } else {
        resolvedColor = '#f50057';
        const num = (mapObj.value ?? 0) + 1;
        iconUrl = createRouteStopIconUrl(resolvedColor, num);
        iconKey = `${resolvedColor}-${num}-white`;
      }
    }
  } else {
    // Derive color from eventName first — it reflects the current unit state
    // (Drive, Idle, Stop, etc.) and is always up-to-date.  mapObj.color can be
    // stale from a previous event update (e.g., unit switched from Idle to Drive
    // but mapObj.color still holds the old gray value).
    const eventColor = resolveEventColor(mapObj.eventName || mapObj.event);
    if (eventColor && eventColor !== '#d7d7d6') {
      // Event name maps to a known color — use it as the authoritative source
      resolvedColor = eventColor;
    } else if (mapObj.color && mapObj.color !== '#d7d7d6' && mapObj.color !== '#FFFFFF') {
      // No specific event color but an explicit non-default color exists — keep it
      resolvedColor = mapObj.color;
    } else if (existingUnit?.color && existingUnit.color !== '#d7d7d6') {
      // Enrichment chunk has no color/event — preserve previous known color
      resolvedColor = existingUnit.color;
    } else {
      resolvedColor = '#d7d7d6';
    }
  }

  // Defensive heading: if enrichment chunk omits heading, preserve the
  // last known value instead of resetting to 0 (which causes 360° spin).
  // Use Number() + isFinite guard to prevent NaN from empty strings or
  // non-numeric values propagating into the deck.gl attribute buffer.
  let heading;
  if (mapObj.heading != null) {
    const parsed = parseInt(mapObj.heading, 10);
    heading = Number.isFinite(parsed) ? parsed : (existingUnit?.heading || 0);
  } else {
    heading = existingUnit?.heading || 0;
  }

  return {
    id: String(mapObj.id),
    coordinates: [lng, lat],
    heading,
    color: resolvedColor,
    iconUrl,
    iconKey,
    useRotation,
    isRouteStop,
    hasVideo: !!mapObj.hasVideo,
    visible: mapObj.enable !== false && !mapObj.hideMarker,
    label: mapObj.label != null ? String(mapObj.label) : (existingUnit?.label ?? ''),
    labelDriver: mapObj.labelDriver != null ? String(mapObj.labelDriver) : (existingUnit?.labelDriver ?? ''),
    // Carry reference to original mapObject for click handler
    _mapObj: mapObj,
  };
};

/* ─────────── WebGLMarkerLayer Class ─────────── */

class WebGLMarkerLayer {
  /**
   * @param {google.maps.Map} map  The Google Maps instance
   */
  constructor(map) {
    this._map = map;
    this._mapTypeListener = null;
    try {
      // interleaved: false → renders deck.gl as a separate DOM canvas
      // instead of blending into the vector map's WebGL pipeline.
      // However, deck.gl places this canvas in Google Maps' "overlayLayer"
      // pane (z-index ~101), which is BELOW the "markerLayer" pane (~103)
      // where landmark markers sit.  We fix this in _raiseCanvasAboveMarkers.
      //
      // useDevicePixels must be an explicit number — passing `true` relies on
      // deck.gl reading window.devicePixelRatio at init time, but the
      // GoogleMapsOverlay may propagate `undefined` via Object.assign and
      // silently fall back to 1x.  Passing the numeric DPR (minimum 2)
      // guarantees the WebGL drawing buffer is always at least 2× CSS pixels,
      // eliminating blurry markers/labels on HiDPI/Retina screens.
      const _dpr = Math.max(window.devicePixelRatio || 1, 2);
      this._overlay = new GoogleMapsOverlay({ interleaved: false, _animate: true, useDevicePixels: _dpr });
      this._overlay.setMap(map);
      this._raiseCanvasAboveMarkers();
      // When switching map types (especially to satellite), Google Maps may
      // append new DOM elements to the map div AFTER the deck.gl container,
      // which renders them on top regardless of z-index.  On `maptypeid_changed`
      // we re-append the deck.gl container to the end of the map div so it
      // stays above any newly inserted tile layers.  After the map finishes
      // loading tiles (`idle`), we also force a deck.gl repaint so the markers
      // are rendered with the correct satellite projection.
      this._mapTypeListener = google.maps.event.addListener(map, 'maptypeid_changed',
        () => {
          this._raiseCanvasAboveMarkers();
          // One-time idle listener: re-raise and force repaint once Google Maps
          // has finished loading the new tile set and committed the final DOM.
          google.maps.event.addListenerOnce(map, 'idle', () => {
            this._raiseCanvasAboveMarkers();
            this._requestOverlayRedraw();
          });
        }
      );
    } catch (err) {
      console.error('[WebGL] Failed to create overlay:', err);
    }

    this._data = [];
    this._hidden = false;
    /**
     * When true, unit markers (arrows/labels/clusters) are suppressed
     * but route-stop pins continue to render.  Used by showVehicleTrails
     * to hide fleet markers while keeping route-stop pins visible during
     * route drawing.
     */
    this._hideUnitsOnly = false;
    /**
     * When set (non-null), this unit ID is kept visible even while
     * _hideUnitsOnly is active.  Used to show the driver's assigned
     * unit on the map when viewing an assigned route.
     */
    this._pinnedUnitId = null;
    /**
     * ID of the currently selected (magnified) route stop.
     * When set, that marker renders at 48px; all others at 32px.
     */
    this._selectedRouteStopId = null;
    this._onClick = null;
    this._onRouteStopClick = null;
    this._onHover = null;
    this._tooltipEl = null;

    /**
     * True when the cursor is hovering over a unit marker in deck.gl.
     * Updated on every mousemove via onHover.
     */
    this._hasHoveredMarker = false;

    /**
     * Timestamp (Date.now()) of the last unit marker click in deck.gl.
     * Landmark click handlers in googleProvider defer their execution by
     * 50ms and check this timestamp — if it's within the last 300ms they
     * suppress their popup so the unit click wins.
     */
    this._unitClickedAt = 0;
    this._dataVersion = 0;
    this._positionVersion = 0;
    this._headingVersion = 0;
    /** Stores last resolved getAngle value per unit for shortest-path rotation */
    this._lastAngleMap = new Map();
    /** Stores previous deck data by ID for state preservation across enrichment merges */
    this._prevStateMap = new Map();
    this._cachedLabelLayers = null;
    /** Counts position-only updates to throttle label rebuilds during animation */
    this._positionUpdateCount = 0;
    /** Tracks how many markers are currently being animated via rAF */
    this._animatingCount = 0;
    /** IDs of markers currently being animated — their labels are hidden */
    this._animatingIds = new Set();
    /** IDs of markers whose animation just ended — labels fade in */
    this._fadingInIds = new Set();
    this._fadeInTimer = null;

    /** Watchdog timer — polls deck.gl layer.isLoaded until atlas is ready */
    this._iconWatchdogTimer = null;

    /** Batch update support — prevents multiple _renderLayers per frame */
    this._batchDepth = 0;
    this._batchNeedsRender = false;

    // No rAF gate needed: the "always-present layers" design ensures
    // deck.gl never destroys/recreates IconLayer instances, so multiple
    // setProps calls per frame are safe (no atlas churn).  Synchronous
    // rendering guarantees deck.gl paints after Google Maps' draw() cycle.

    /** Collision detection cache — avoids re-running when viewport hasn't changed */
    this._collisionCacheKey = '';
    this._collisionCacheResult = null;

    // Suppress transitions on the first non-empty data render to avoid
    // any initial "fly" artifacts when buffers start at zero.
    this._suppressTransitionsOnce = false;

    /** Label settings controlled by unitLabelSettings */
    // Default to showing unit labels so the initial load never renders
    // markers without labels if settings arrive slightly later.
    this._labelSettings = { enabled: true, showUnit: true, showDriver: false, darkLabels: false };

    /** Pure-WebGL no-overlap label layout (only used when clusters are OFF) */
    this._noOverlapLabels = { enabled: false, minZoom: 8 };
    this._onLabelGroupClick = null;
    this._noOverlapGroupLabels = []; // [{ coordinates, displayNames, unitIds, count, hasMore }]

    /** Translatable "View Detail..." text injected from the provider */
    this._viewDetailText = 'View Detail...';

    /** Label grouping: groups nearby labels into DOM overlays when clusters are OFF */
    this._labelGroupingEnabled = false;
    this._labelGroups = []; // [{ center: [lng,lat], names: [], unitData: [], count }]

    /** Clustering state */
    this._clusterEnabled = false;
    this._clusterOptions = { maxZoom: 18, radius: 200 };
    this._supercluster = null;
    this._zoom = 1;
    this._clusterFeatures = []; // GeoJSON features from supercluster
    this._clusterVersion = 0; // monotonic counter — incremented on every _computeClusters

    /** Periodic cluster reconciliation — keeps cluster membership up-to-date
     *  when markers move via socket/animation between idle events. */
    this._clustersDirty = false;
    this._lastClusterRebuild = 0;
    this._clusterReconcileTimer = null;

    /**
     * Cluster transition state machine:
     *
     * When a user clicks a cluster icon, `map.setZoom()` + `map.panTo()` fire
     * synchronously but Google Maps' camera doesn't actually move until the
     * next animation frame.  `getBounds()` still returns the OLD viewport at
     * the moment setZoom fires, so `_computeClusters` produces stale features.
     *
     * `_clusterTransitionPending` marks that a zoom/pan was initiated and the
     * cluster index needs recomputation once the map reaches `idle`.
     *
     * `_deferredClusterRecompute` holds the timer ID for a deferred
     * recomputation — a failsafe that fires after 600ms even if `idle`
     * doesn't arrive (e.g., when the camera is already at the target).
     */
    this._clusterTransitionPending = false;
    this._deferredClusterRecompute = null;

    /**
     * Monotonic epoch: incremented on every enableClustering/disableClustering.
     * Deferred callbacks check this against their captured epoch to discard
     * stale renders that would fight with the current cluster state.
     */
    this._clusterEpoch = 0;

    /**
     * Last `_data.length` that was successfully rendered.  Used by the
     * post-render coherence check to detect when the GPU-side buffer
     * is out of sync with the source data.
     */
    this._lastRenderedDataCount = 0;

    /**
     * Monotonic epoch for route-stop icon atlas cache busting.
     * Incremented every time route-stop data transitions from empty to non-empty.
     * When deck.gl's IconManager garbage-collects atlas entries (data → []) then
     * sees the same icon URLs return, it skips re-loading because the icon ID
     * matches its internal cache — but the GPU texture has been freed.
     * Including the epoch in the icon ID forces a fresh atlas load.
     */
    this._routeStopEpoch = 0;
    /** Previous route stop count — used to detect 0 → N transitions */
    this._prevRouteStopCount = 0;

    /**
     * Font atlas version — bumped on tab focus restore to force deck.gl
     * to create fresh TextLayer instances with new FontAtlasManager and
     * GPU textures.  Without this, the cached SDF font atlas texture may
     * be evicted by the GPU while the tab is hidden, causing white boxes
     * with no text on tab restore.
     */
    this._fontAtlasVersion = 0;

    this._createTooltip();
  }

  /* ─── Public API ─── */

  /**
   * Update the displayed markers from mapObjects array.
   * @param {Array} mapObjects  Array of internal MapObject items
   *                            (filtered to layerName == Units and enable == true)
   */
  updateMarkers(mapObjects) {
    const wasEmpty = this._data.length === 0;

    // Single-pass: map + filter in one loop avoids intermediate array allocation
    const result = [];
    let headingChanged = false;
    for (let i = 0, len = mapObjects.length; i < len; i++) {
      const unitId = String(mapObjects[i].id);
      const existingUnit = this._prevStateMap.get(unitId);

      const d = mapObjectToDeckData(mapObjects[i], existingUnit);
      if (!d.visible) {
        continue;
      }
      const [lng, lat] = d.coordinates;

      // Filter truly invalid coordinates — includes NaN/Infinity
      // which can corrupt the deck.gl attribute buffer and hide ALL markers.
      if (lat === 0 && lng === 0) {
        continue;
      }
      if (lat == null || lng == null) {
        continue;
      }
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        continue;
      }

      // Detect whether heading actually changed for this unit
      if (existingUnit && existingUnit.heading !== d.heading) {
        headingChanged = true;
      }
      result.push(d);
    }

    // Stable ordering prevents deck.gl transitions from mismatching objects
    // by index (which can look like a marker flying across the world).
    result.sort((a, b) => String(a.id).localeCompare(String(b.id), undefined, { numeric: true, sensitivity: 'base' }));

    // Rebuild previous-state map for next merge cycle
    this._prevStateMap.clear();
    for (let i = 0, len = result.length; i < len; i++) {
      this._prevStateMap.set(result[i].id, result[i]);
    }

    // Prune stale entries from the resolved-angle map
    const currentIds = new Set(result.map(d => d.id));
    for (const k of this._lastAngleMap.keys()) {
      if (!currentIds.has(k)) this._lastAngleMap.delete(k);
    }

    // ── Icon atlas cache-bust: detect route stop reappearance ──
    const routeStopCount = result.filter(d => d.isRouteStop).length;
    //
    // When route stops transition from 0 → N, deck.gl's IconManager
    // may have GC'd the previous atlas entries but still "knows" the
    // icon IDs.  Bumping the epoch makes new IDs, forcing a reload.
    if (routeStopCount > 0 && this._prevRouteStopCount === 0) {
      // Toggle epoch between 0 and 1 instead of incrementing indefinitely.
      // Unlimited growth caused icon atlas texture exhaustion after many
      // route loads because every epoch produced brand-new icon IDs that
      // accumulated as separate atlas entries in the GPU texture.
      this._routeStopEpoch = (this._routeStopEpoch + 1) % 2;
    }
    this._prevRouteStopCount = routeStopCount;

    this._data = result;
    this._dataVersion += 1;
    this._positionVersion += 1;
    // Only bump heading version when a heading actually changed —
    // prevents enrichment-only updates from restarting angle transitions.
    if (headingChanged || wasEmpty) {
      this._headingVersion += 1;
    }
    this._cachedLabelLayers = null;
    this._cachedAnimationLabelLayer = null;
    this._collisionCacheKey = ''; // invalidate collision cache on data change
    this._positionUpdateCount = 0;

    // If this is the first time we render with data, suppress transitions
    // for one render pass so markers spawn directly at their real position.
    if (wasEmpty && result.length > 0) {
      this._suppressTransitionsOnce = true;
    }

    // Full data rebuild resets the dirty flag — clusters are now fresh
    this._clustersDirty = false;
    this._lastClusterRebuild = performance.now();

    // Reload supercluster index when data changes during clustering
    if (this._clusterEnabled && this._supercluster) {
      this._loadClusterIndex();
    }

    this._scheduleRender();
  }

  /**
   * Efficiently update a single marker's position without rebuilding all data.
   * Used during animation frames to move the WebGL marker smoothly.
   * @param {string|number} id  Marker ID
   * @param {number} lng  Longitude
   * @param {number} lat  Latitude
   */
  /**
   * Notify the layer that a rAF animation has started for a marker.
   * While animations are active, _renderLayers suppresses deck.gl
   * transitions to avoid angle/position jitter from transition conflicts.
   * The marker's label is hidden during animation to avoid lag.
   */
  startAnimation(id) {
    const strId = id != null ? String(id) : null;
    // Only increment count when a NEW id starts animating.
    // Prevents _animatingCount from growing unbounded when
    // the same unit receives rapid consecutive animation events.
    if (strId && !this._animatingIds.has(strId)) {
      this._animatingCount += 1;
      this._animatingIds.add(strId);

      // Snapshot the current heading into _lastAngleMap so that
      // _resolveAngle always has a valid baseline during animation.
      // Without this, the first animation-frame render could miss
      // the cached angle and fall back to 0.
      const entry = this._data.find(d => d.id === strId);
      if (entry && !this._lastAngleMap.has(strId)) {
        this._lastAngleMap.set(strId, -(entry.heading || 0));
      }

      // Build the animation label cache immediately so _renderPositionUpdate
      // can hide labels for this marker on the very next rAF frame.
      // _renderLayers() normally builds this cache, but it runs BEFORE
      // startAnimation() is called — leaving _cachedAnimationLabelLayer null.
      // Without this, labels remain visible during the entire trail animation.
      if (!this._clusterEnabled && this._data.length > 0) {
        const staticData = this._data.filter(d => !this._animatingIds.has(String(d.id)));
        this._cachedAnimationLabelLayer = staticData.length > 0
          ? this._createLabelTextLayer(staticData, 'webgl-unit-labels-static')
          : null;
      }
    }
  }

  /**
   * Notify the layer that a rAF animation has completed for a marker.
   * Triggers a label fade-in effect for the marker.
   */
  stopAnimation(id) {
    const strId = id != null ? String(id) : null;
    if (strId && this._animatingIds.has(strId)) {
      this._animatingIds.delete(strId);
      this._animatingCount = Math.max(0, this._animatingCount - 1);
      this._fadingInIds.add(strId);

      // ── Final State Recovery ──
      // Snap the marker to its definitive heading from the latest data.
      // During animation, rapid position-only updates may have left
      // _resolveAngle with a stale cached angle.  Clear the cache entry
      // so the next _resolveAngle call jumps directly to the correct
      // target instead of interpolating from a stale value.
      const entry = this._data.find(d => d.id === strId);
      if (entry) {
        // Guard against undefined/NaN heading left over from the animation
        if (entry.heading == null || Number.isNaN(entry.heading)) {
          entry.heading = 0;
        }
        // Force _resolveAngle to re-resolve from scratch for this unit
        this._lastAngleMap.delete(strId);
      }
      // Bump versions so the IconLayer's getAngle updateTrigger detects
      // the change and re-evaluates _resolveAngle for every marker.
      this._headingVersion += 1;
      this._positionVersion += 1;

      // Clear fading state after the transition completes (~500ms)
      if (this._fadeInTimer) clearTimeout(this._fadeInTimer);
      this._fadeInTimer = setTimeout(() => {
        this._fadingInIds.clear();
        this._fadeInTimer = null;
      }, 600);

      // Trigger a full layer rebuild so the post-animation heading,
      // label visibility, and fade-in transitions are all applied.
      this._cachedLabelLayers = null;
      this._cachedAnimationLabelLayer = null;
      this._scheduleRender();
    }
  }

  /**
   * Clear all animation tracking state.
   * Safety valve: called periodically or on settings change to prevent
   * stale animation IDs from keeping labels permanently transparent.
   */
  clearStaleAnimations() {
    if (this._animatingIds.size === 0) return;
    this._animatingIds.clear();
    this._animatingCount = 0;
    this._fadingInIds.clear();
    if (this._fadeInTimer) {
      clearTimeout(this._fadeInTimer);
      this._fadeInTimer = null;
    }
  }

  updateMarkerPosition(id, lng, lat, heading) {
    // Ignore invalid coordinates — keep the marker at its last valid position
    if ((lng === 0 && lat === 0) || !Number.isFinite(lng) || !Number.isFinite(lat)) return;

    let found = false;
    let headingChanged = false;
    const strId = String(id);
    for (let i = 0, len = this._data.length; i < len; i++) {
      if (this._data[i].id === strId) {
        this._data[i].coordinates = [lng, lat];
        // Always resolve heading: use provided value or preserve existing.
        // Without this, an undefined heading argument could leave the data
        // item in an ambiguous state where _resolveAngle falls back to 0.
        const currentHeading = heading !== undefined ? heading : (this._data[i].heading || 0);
        if (this._data[i].heading !== currentHeading) {
          this._data[i].heading = currentHeading;
          headingChanged = true;
        }
        found = true;
        break;
      }
    }
    if (!found) return;
    this._positionVersion += 1;
    if (headingChanged) {
      this._headingVersion += 1;
    }

    // Mark clusters as potentially stale — a marker moved and may have
    // left or entered a cluster radius.  The periodic reconciliation
    // timer will rebuild the Supercluster index on the next tick.
    if (this._clusterEnabled) {
      this._clustersDirty = true;
    }

    // Lightweight render: only position/heading changed, skip icon/label rebuild
    this._renderPositionUpdate();
  }

  /**
   * Set the click handler for markers.
   * @param {Function} handler  (mapObject, event) => void
   */
  setOnClick(handler) {
    if (this._onClick === handler) return;
    this._onClick = handler;
    this._scheduleRender();
  }

  /**
   * Set a dedicated click handler for route-stop markers.
   * When set, route-stop clicks invoke this instead of the generic onClick.
   * @param {Function|null} handler  (mapObject, deckInfo) => void
   */
  setOnRouteStopClick(handler) {
    this._onRouteStopClick = handler;
  }

  /**
   * Mark a route stop as "selected" (magnified to 48px).
   * Pass null to deselect all route stops (reset to 32px).
   * Triggers a re-render so the size change is visible immediately.
   *
   * @param {string|null} id  The mapObject.id to magnify, or null to reset
   */
  setSelectedRouteStop(id) {
    if (this._selectedRouteStopId === id) return;
    this._selectedRouteStopId = id;
    this._scheduleRender();
  }

  /**
   * Set the hover handler for markers.
   * @param {Function} handler  (mapObject | null, event) => void
   */
  setOnHover(handler) {
    if (this._onHover === handler) return;
    this._onHover = handler;
    this._scheduleRender();
  }

  /**
   * Process a click at the given pixel coordinates (relative to the map div).
   *
   * Uses deck.gl's `pickObject` to detect unit markers or cluster icons via
   * GPU framebuffer readback.  This supplements deck.gl's built-in onClick,
   * which does NOT fire when the deck.gl container has `pointer-events: none`
   * (required by `_raiseCanvasAboveMarkers` so clicks reach native Google Maps
   * elements below the canvas).
   *
   * Called from a capture-phase DOM click handler on the map div so that
   * `_unitClickedAt` is set BEFORE any Google Maps shape/marker click handler
   * fires.  The existing `setTimeout` + `_unitClickedAt` checks in landmark
   * handlers then correctly suppress their popup when a unit was picked.
   *
   * @param {number} x  CSS pixel X relative to map div
   * @param {number} y  CSS pixel Y relative to map div
   * @returns {boolean}  true if a deck.gl object was picked and handled
   */
  handleClick(x, y) {
    // Prevent double-handling if deck.gl's built-in event system already fired
    if (Date.now() - this._unitClickedAt < 100) return false;

    let info;
    try {
      info = this._overlay.pickObject({ x, y, radius: 10 });
    } catch (e) {
      return false;
    }

    if (!info || !info.object) return false;

    const obj = info.object;

    // Unit marker (carries _mapObj reference to the original mapObject)
    if (obj._mapObj) {
      this._unitClickedAt = Date.now();
      if (this._onClick) this._onClick(obj._mapObj, info);
      return true;
    }

    // Cluster icon (carries cluster properties from supercluster)
    if (obj.properties?.cluster && this._supercluster && this._map) {
      this._unitClickedAt = Date.now();
      try {
        const cid = obj.properties.cluster_id;
        const expansionZoom = this._supercluster.getClusterExpansionZoom(cid);
        const [lng, lat] = obj.geometry.coordinates;
        // Mark transition pending BEFORE triggering the camera move.
        // setZoom/panTo fire synchronously but getBounds() still returns
        // the old viewport until the map finishes animating.  The deferred
        // recomputation (+ idle handler) will rebuild clusters with the
        // correct bounds.
        this._clusterTransitionPending = true;
        this._map.setZoom(Math.min(expansionZoom, 20));
        this._map.panTo({ lat, lng });
        this._scheduleDeferredClusterRecompute();
      } catch (e) {
        // Cluster may no longer exist after a data update
        this._clusterTransitionPending = false;
      }
      return true;
    }

    return false;
  }

  /**
   * Process a hover at the given pixel coordinates (relative to the map div).
   *
   * Like `handleClick`, this supplements deck.gl's built-in onHover which
   * cannot fire when `pointer-events: none` is set on the overlay container.
   * Called from a throttled mousemove listener on the map div.
   *
   * @param {number} x  CSS pixel X relative to map div (for pickObject)
   * @param {number} y  CSS pixel Y relative to map div (for pickObject)
   * @param {number} [pageX]  Page-relative X for tooltip positioning
   * @param {number} [pageY]  Page-relative Y for tooltip positioning
   */
  handleHover(x, y, pageX, pageY) {
    let info;
    try {
      info = this._overlay.pickObject({ x, y, radius: 5 });
    } catch (e) {
      return;
    }

    const tipX = pageX ?? x;
    const tipY = pageY ?? y;

    if (info && info.object) {
      this._hasHoveredMarker = true;
      const label = info.object.label || info.object.id;
      if (label && this._tooltipEl) {
        this._tooltipEl.textContent = label;
        this._tooltipEl.style.display = 'block';
        this._tooltipEl.style.left = `${tipX + 12}px`;
        this._tooltipEl.style.top = `${tipY - 12}px`;
      }
      // Change cursor to pointer when over a pickable object
      if (this._map) {
        this._map.setOptions({ draggableCursor: 'pointer' });
      }
    } else {
      if (this._hasHoveredMarker) {
        this._hasHoveredMarker = false;
        if (this._tooltipEl) this._tooltipEl.style.display = 'none';
        if (this._map) {
          this._map.setOptions({ draggableCursor: null });
        }
      }
    }
  }

  /**
   * Set the translated text for "View Detail..." links.
   * Call this whenever the locale changes so WebGL labels stay in sync.
   * @param {string} text  e.g. messages['map_view_detail']
   */
  setViewDetailText(text) {
    if (text && typeof text === 'string') {
      this._viewDetailText = text;
    }
  }

  /**
   * Set click handler for group labels (5+ items) in no-overlap mode.
   * @param {Function|null} handler (unitIds: number[], meta: { count: number }) => void
   */
  setOnLabelGroupClick(handler) {
    this._onLabelGroupClick = handler;
  }

  /**
   * Returns the placed 5+ label groups from the last no-overlap layout pass.
   * Intended for creating a small number of DOM MarkerOverlay bubbles that
   * reuse the existing cluster label UI ("View Detail...").
   */
  getNoOverlapGroupLabels() {
    return this._noOverlapGroupLabels;
  }

  /**
   * Get the individual unit data items inside a cluster.
   * @param {number} clusterId  The cluster_id from supercluster
   * @param {number} [limit=Infinity]  Max number of leaves to return
   * @returns {Array}  Array of original mapObject references from the cluster leaves
   */
  getClusterLeaves(clusterId, limit = Infinity) {
    if (!this._supercluster) return [];
    try {
      const leaves = this._supercluster.getLeaves(clusterId, limit, 0);
      return leaves.map(leaf => leaf.properties._mapObj).filter(Boolean);
    } catch (e) {
      return [];
    }
  }

  /**
   * Return the current cluster features for external label overlay creation.
   * Each item: { clusterId, coordinates: [lng, lat], pointCount }
   * @returns {Array}
   */
  getClusters() {
    if (!this._clusterEnabled || !this._clusterFeatures) return [];
    const result = [];
    for (let i = 0, len = this._clusterFeatures.length; i < len; i++) {
      const f = this._clusterFeatures[i];
      if (f.properties.cluster) {
        result.push({
          clusterId: f.properties.cluster_id,
          coordinates: f.geometry.coordinates, // [lng, lat]
          pointCount: f.properties.point_count,
        });
      }
    }
    return result;
  }

  /* ─── Batch Update API ─── */

  /**
   * Begin a batch update. While batching, calls to _renderLayers are
   * deferred until endUpdate() so that multiple state changes (label
   * settings + no-overlap + zoom) result in a single render pass.
   */
  beginUpdate() {
    this._batchDepth++;
  }

  /**
   * End a batch update. Triggers a deferred _renderLayers if any
   * state changes occurred during the batch.
   */
  endUpdate() {
    this._batchDepth = Math.max(0, this._batchDepth - 1);
    if (this._batchDepth === 0 && this._batchNeedsRender) {
      this._batchNeedsRender = false;
      this._scheduleRender();
    }
  }

  /**
   * Update label display settings (unit name, driver name, enabled).
   * @param {{ enabled: boolean, showUnit: boolean, showDriver: boolean }} settings
   * @param {boolean} [forceRender=false] When true, re-render even if settings
   *        haven't changed. Useful after zoom/pan when the viewport moved but
   *        label collision detection needs to re-run.
   */
  setLabelSettings(settings, forceRender = false) {
    const changed =
      this._labelSettings.enabled !== settings.enabled ||
      this._labelSettings.showUnit !== settings.showUnit ||
      this._labelSettings.showDriver !== settings.showDriver ||
      this._labelSettings.darkLabels !== settings.darkLabels;
    this._labelSettings = { ...settings };
    if (!changed && !forceRender) return;
    this._dataVersion += 1;
    this._collisionCacheKey = ''; // invalidate collision cache
    this._scheduleRender();
  }

  /**
   * Enable/disable no-overlap label layout (pure WebGL) for non-clustered mode.
   * This does NOT affect clustered mode.
   *
   * @param {{ enabled: boolean, minZoom?: number }} opts
   */
  setNoOverlapLabels(opts) {
    const enabled = Boolean(opts?.enabled);
    const minZoom = Number.isFinite(opts?.minZoom) ? Math.floor(opts.minZoom) : this._noOverlapLabels.minZoom;

    const changed =
      this._noOverlapLabels.enabled !== enabled ||
      this._noOverlapLabels.minZoom !== minZoom;
    if (!changed) return;

    this._noOverlapLabels = { enabled, minZoom };
    this._scheduleRender();
  }

  /**
   * Enable or disable label grouping.
   * When active (clusters OFF + "Label Clusters" ON), nearby labels are
   * grouped into DOM overlays instead of individual TextLayer items.
   * @param {boolean} enabled
   */
  setLabelGrouping(enabled) {
    const wasEnabled = this._labelGroupingEnabled;
    this._labelGroupingEnabled = Boolean(enabled);
    if (wasEnabled !== this._labelGroupingEnabled) {
      if (!this._labelGroupingEnabled) this._labelGroups = [];
      this._scheduleRender();
    }
  }

  /**
   * Return the current label groups computed during the last render.
   * Each group has: { center: [lng,lat], names: string[], unitData: Object[], count: number }
   * Used by googleProvider to create DOM-based group label overlays.
   * @returns {Array}
   */
  getLabelGroups() {
    return this._labelGroups;
  }

  /**
   * Get the visible (unclustered) unit data items with their mapObject references.
   * Used by googleProvider to create DOM-based label overlays.
   * In clustered mode, only unclustered singles are returned.
   * @returns {Array<{ coordinates: [number, number], _mapObj: Object }>}
   */
  getVisibleUnits() {
    if (this._clusterEnabled) {
      const singles = [];
      for (let i = 0, len = this._clusterFeatures.length; i < len; i++) {
        const f = this._clusterFeatures[i];
        if (!f.properties.cluster) {
          singles.push(f.properties);
        }
      }
      return singles;
    }
    return this._data;
  }

  /* ─── Clustering API ─── */

  /**
   * Enable supercluster-based clustering on the WebGL layer.
   * @param {{ maxZoom?: number, radius?: number }} options
   */
  enableClustering(options = {}) {
    this._clusterEpoch++;
    this._cancelDeferredClusterRecompute();
    this._clusterOptions = { ...this._clusterOptions, ...options };
    this._clusterEnabled = true;
    this._clusterTransitionPending = false;
    this._supercluster = new Supercluster({
      minZoom: 0,
      maxZoom: this._clusterOptions.maxZoom,
      radius: this._clusterOptions.radius,
    });
    // Read current zoom from map (floor to integer — supercluster uses
    // integer zoom levels internally and setZoom also floors).
    if (this._map) {
      this._zoom = Math.floor(this._map.getZoom()) || 1;
    }
    this._loadClusterIndex();
    this._scheduleRender();

    // Start periodic cluster reconciliation (every 60s)
    this._startClusterReconcileTimer();
  }

  /**
   * Disable clustering and return to flat marker rendering.
   */
  disableClustering() {
    this._clusterEpoch++;
    this._cancelDeferredClusterRecompute();
    this._clusterEnabled = false;
    this._clusterTransitionPending = false;
    this._supercluster = null;
    this._clusterFeatures = [];
    this._clustersDirty = false;
    this._stopClusterReconcileTimer();
    this._scheduleRender();
  }

  /**
   * Update the zoom level for cluster recomputation.
   * Called from googleProvider's idle handler on zoom/pan.
   * @param {number} zoom  Current Google Maps zoom level
   * @param {boolean} [render=true]  When false, only update the internal zoom
   *        value without re-computing clusters or re-rendering.  Pass false
   *        when an `updateMarkers()` call will follow immediately — that call
   *        reloads the supercluster index and renders, making the intermediate
   *        render from setZoom redundant.
   */
  setZoom(zoom, render = true) {
    const z = Math.floor(zoom);
    if (this._zoom === z) return;
    this._zoom = z;
    this._collisionCacheKey = ''; // viewport changed, invalidate collision cache
    if (this._clusterEnabled && this._supercluster) {
      this._computeClusters();
    }
    if (!render) return;
    this._scheduleRender();
  }

  /**
   * @returns {boolean} Whether clustering is currently enabled.
   */
  isClusteringEnabled() {
    return this._clusterEnabled;
  }

  /**
   * Force re-render (e.g., after icon changes or viewport pan).
   * Invalidates the collision cache so labels are recomputed.
   */
  refresh() {
    this._collisionCacheKey = '';
    this._scheduleRender();
  }

  /**
   * Public force-refresh: invalidates ALL cached state and guarantees a
   * full layer rebuild on the next animation frame.  Call this when the
   * cluster state may have become inconsistent (e.g., after rapid
   * zoom/click interactions) to recover from invisible markers.
   *
   * Also runs a data/GPU coherence check: if the last rendered marker
   * count doesn't match the current source data, a full cluster index
   * rebuild is forced regardless of dirty flags.
   *
   * Safe to call multiple times — only one render per frame.
   */
  invalidate() {
    this._collisionCacheKey = '';
    this._cachedLabelLayers = null;
    this._cachedAnimationLabelLayer = null;
    this._clusterTransitionPending = false;
    if (this._clusterEnabled && this._supercluster && this._data.length > 0) {
      this._loadClusterIndex();
    }
    // Data/GPU coherence: if the marker count diverged from the last
    // render pass, force a full index rebuild + render.
    if (this._lastRenderedDataCount !== this._data.length) {
      if (this._clusterEnabled && this._supercluster) {
        this._loadClusterIndex();
      }
    }
    this._scheduleRender();
  }

  /**
   * Show/hide the entire WebGL layer.
   * Instead of detaching/reattaching the overlay (which breaks deck.gl
   * internal state), we render empty layers to hide and re-render to show.
   */
  setVisible(visible) {
    this._hidden = !visible;
    this._hideUnitsOnly = false;
    if (visible) {
      // Re-render with current data
      this._scheduleRender();
    } else {
      // Cancel any pending rAF render and clear layers immediately
      this._cancelScheduledRender();
      this._overlay.setProps({ layers: [] });
    }
  }

  /**
   * Show/hide only unit (vehicle) markers while keeping route-stop pins
   * visible.  Used during route drawing (showVehicleTrails) to suppress
   * fleet arrows without hiding the route-stop pins that live on the
   * same WebGL overlay.
   *
   * @param {boolean} visible  true = show units, false = hide units only
   */
  setUnitsVisible(visible) {
    if (visible) {
      this._hideUnitsOnly = false;
      this._pinnedUnitId = null;
      this._hidden = false;
      this._scheduleRender();
    } else {
      this._hideUnitsOnly = true;
      this._hidden = false;  // overlay stays active for route stops
      this._scheduleRender();
    }
  }

  /**
   * Pin a specific unit so it remains visible when _hideUnitsOnly is active.
   * Used to show the driver's assigned vehicle on the map while viewing
   * an assigned route with live event updates.
   *
   * @param {string|number|null} id  The unit/marker ID to keep visible, or null to clear
   */
  setPinnedUnitId(id) {
    this._pinnedUnitId = id != null ? String(id) : null;
    this._scheduleRender();
  }

  /**
   * Remove the overlay from the map and clean up.
   */
  destroy() {
    if (this._hasHoveredMarker && this._map) {
      this._map.setOptions({ draggableCursor: null });
    }
    this._cancelScheduledRender();
    this._cancelDeferredClusterRecompute();
    this._stopClusterReconcileTimer();
    if (this._iconWatchdogTimer) {
      clearTimeout(this._iconWatchdogTimer);
      this._iconWatchdogTimer = null;
    }
    if (this._mapTypeListener) {
      google.maps.event.removeListener(this._mapTypeListener); // eslint-disable-line no-undef
      this._mapTypeListener = null;
    }
    if (this._overlay) {
      this._overlay.setMap(null);
      this._overlay.finalize();
    }
    this._data = [];
    this._lastAngleMap.clear();
    this._prevStateMap.clear();
    this._clusterEnabled = false;
    this._clusterTransitionPending = false;
    this._supercluster = null;
    this._clusterFeatures = [];
    if (this._tooltipEl) {
      this._tooltipEl.remove();
      this._tooltipEl = null;
    }
  }

  /* ─── Cluster Reconciliation ─── */

  /**
   * Start the periodic cluster reconciliation timer (every 60s).
   * Only runs while clustering is active.  Checks if marker positions
   * have changed since the last Supercluster rebuild and, if so,
   * reloads the index so cluster membership and counts stay accurate.
   * @private
   */
  _startClusterReconcileTimer() {
    if (this._clusterReconcileTimer) return; // avoid duplicate timers
    this._clusterReconcileTimer = setInterval(() => {
      this._reconcileClustersIfDirty();
    }, 60000);
  }

  /**
   * Stop the periodic cluster reconciliation timer.
   * Called when clustering is disabled, the tab loses focus, or the
   * layer is destroyed.
   * @private
   */
  _stopClusterReconcileTimer() {
    if (this._clusterReconcileTimer) {
      clearInterval(this._clusterReconcileTimer);
      this._clusterReconcileTimer = null;
    }
  }

  /**
   * Periodic cluster reconciliation (runs every 60 seconds).
   *
   * When markers move via updateMarkerPosition() (socket updates / rAF
   * animation), the Supercluster index becomes stale — markers may have
   * left or entered cluster radii.  This method rebuilds the index from
   * current _data positions so cluster membership and counts stay
   * accurate without impacting per-frame animation performance.
   *
   * Deferred while trail animations are active to avoid visual jitter.
   * @private
   */
  _reconcileClustersIfDirty() {
    if (!this._clustersDirty || !this._clusterEnabled) return;
    // Defer while animations are running to avoid cluster jitter
    if (this._animatingIds && this._animatingIds.size > 0) return;

    this._clustersDirty = false;
    this._lastClusterRebuild = performance.now();

    // Rebuild the Supercluster index from current _data coordinates
    this._loadClusterIndex();
    this._collisionCacheKey = ''; // invalidate collision cache
    this._scheduleRender();
  }

  /**
   * Force all TextLayers to rebuild their SDF font atlas on the next render.
   *
   * When the browser tab loses focus the GPU may evict WebGL textures,
   * including the SDF font atlas used by deck.gl TextLayer.  On tab
   * restore, deck.gl reuses the cached FontAtlasManager (same layer ID),
   * but the underlying GPU texture is gone — resulting in white label
   * boxes with no text.
   *
   * Bumping `_fontAtlasVersion` changes every TextLayer ID, so deck.gl
   * creates brand-new layer instances with fresh FontAtlasManagers that
   * upload new GPU textures.
   *
   * Call this from the tab-focus-restore path BEFORE triggering a render.
   */
  invalidateFontAtlas() {
    this._fontAtlasVersion++;
    this._collisionCacheKey = ''; // force collision filter rebuild
  }

  /**
   * Pause cluster reconciliation (e.g., when the browser tab loses focus).
   * The timer is restarted via resumeClusterReconcile() on focus restore.
   */
  pauseClusterReconcile() {
    this._stopClusterReconcileTimer();
  }

  /**
   * Resume cluster reconciliation (e.g., when the browser tab regains focus).
   * Only starts the timer if clustering is currently enabled.
   */
  resumeClusterReconcile() {
    if (this._clusterEnabled) {
      this._startClusterReconcileTimer();
    }
  }

  /* ─── Internal: Clustering ─── */

  /**
   * Load all current data points into the supercluster index.
   * Must be called whenever _data changes while clustering is enabled.
   */
  _loadClusterIndex() {
    if (!this._supercluster || !this._data.length) {
      this._clusterFeatures = [];
      return;
    }
    const points = [];
    for (let i = 0, len = this._data.length; i < len; i++) {
      const d = this._data[i];
      // Route stops must never enter the Supercluster index — they are
      // always rendered individually with their status pin icon.
      if (d.isRouteStop) continue;
      points.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: d.coordinates },
        properties: d,
      });
    }
    this._supercluster.load(points);
    this._computeClusters();
  }

  /**
   * Recompute clusters at the current zoom level from the loaded index.
   * Uses the current map viewport (with padding) so supercluster returns
   * the correct cluster decomposition for the visible area.
   *
   * When a cluster transition is pending (click-to-zoom), the viewport
   * bounds are stale.  In that case we use the maximum bbox [-180..180]
   * so no markers are lost, and schedule a precise recomputation for
   * when the map reaches idle.
   */
  _computeClusters() {
    if (!this._supercluster) return;
    // If a cluster click just fired setZoom+panTo, getBounds() still
    // returns the old camera — use world extent as a safe fallback.
    const bbox = this._clusterTransitionPending
      ? [-180, -85, 180, 85]
      : this._getViewportBounds();
    this._clusterFeatures = this._supercluster.getClusters(bbox, this._zoom);
    this._clusterVersion++;
  }

  /**
   * Schedule a deferred cluster recomputation after a camera transition.
   *
   * Cluster clicks call setZoom + panTo which animate over ~300-600ms.
   * During that window getBounds() returns the old viewport, so
   * _computeClusters would produce stale features.
   *
   * This method schedules a rebuild after 600ms — long enough for most
   * camera animations to settle.  The Google Maps `idle` event also
   * calls `resolveClusterTransition()` which cancels this timer and
   * does an immediate rebuild with correct bounds.
   *
   * The epoch guard ensures that if the user toggles clustering OFF/ON
   * during the wait, the stale callback is discarded.
   * @private
   */
  _scheduleDeferredClusterRecompute() {
    this._cancelDeferredClusterRecompute();
    const epoch = this._clusterEpoch;
    this._deferredClusterRecompute = setTimeout(() => {
      this._deferredClusterRecompute = null;
      if (this._clusterEpoch !== epoch) return; // stale — clustering toggled
      this.resolveClusterTransition();
    }, 600);
  }

  /**
   * Cancel any pending deferred cluster recomputation.
   * @private
   */
  _cancelDeferredClusterRecompute() {
    if (this._deferredClusterRecompute) {
      clearTimeout(this._deferredClusterRecompute);
      this._deferredClusterRecompute = null;
    }
  }

  /**
   * Resolve a pending cluster transition.
   *
   * Called by googleProvider's `idle` handler (or the 600ms failsafe)
   * once the map camera has settled after a cluster click.  Recomputes
   * clusters with the now-correct viewport bounds and forces a full
   * layer rebuild so all markers become visible immediately.
   *
   * Safe to call multiple times — no-ops if no transition is pending.
   */
  resolveClusterTransition() {
    this._cancelDeferredClusterRecompute();
    if (!this._clusterTransitionPending) return;
    this._clusterTransitionPending = false;
    if (!this._clusterEnabled || !this._supercluster) return;

    // Now getBounds() returns the correct post-animation viewport.
    this._computeClusters();
    this._collisionCacheKey = '';
    this._scheduleRender();
  }

  /**
   * Return the current map viewport as a [west, south, east, north] bbox
   * suitable for supercluster.getClusters().  Adds ~25% padding so clusters
   * near the viewport edge don't pop in/out during small pans.
   * Falls back to world extent when bounds are unavailable.
   * @returns {number[]}
   * @private
   */
  _getViewportBounds() {
    if (!this._map) return [-180, -85, 180, 85];
    try {
      const bounds = this._map.getBounds();
      if (!bounds) return [-180, -85, 180, 85];
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      const lngSpan = ne.lng() - sw.lng();
      const latSpan = ne.lat() - sw.lat();
      const pad = 0.25;
      return [
        sw.lng() - lngSpan * pad,
        Math.max(sw.lat() - latSpan * pad, -85),
        ne.lng() + lngSpan * pad,
        Math.min(ne.lat() + latSpan * pad, 85),
      ];
    } catch (e) {
      return [-180, -85, 180, 85];
    }
  }

  /* ─── Internal: Tooltip ─── */

  _createTooltip() {
    this._tooltipEl = document.createElement('div');
    Object.assign(this._tooltipEl.style, {
      position: 'absolute',
      zIndex: '9999',
      pointerEvents: 'none',
      padding: '4px 8px',
      background: 'rgba(0,0,0,0.75)',
      color: 'white',
      borderRadius: '4px',
      fontSize: '12px',
      fontFamily: 'Roboto, Arial, sans-serif',
      whiteSpace: 'nowrap',
      display: 'none',
      transition: 'opacity 0.15s',
    });
    document.body.appendChild(this._tooltipEl);
  }

  /* ─── Internal: Canvas Z-Index ─── */

  /**
   * Place the deck.gl marker canvas inside `.gm-style`, immediately after
   * the Google Maps panes container.
   *
   * Background
   * ──────────
   * On vector maps (mapId), deck.gl with `interleaved: false` appends its
   * canvas as a direct child of `mapDiv`, OUTSIDE `.gm-style`.
   * `.gm-style` itself uses `z-index: auto`, so any explicit `z-index` on
   * the deck.gl container (e.g. 104) makes it win against `.gm-style` and
   * against everything painted inside it — including the custom map control
   * buttons that Google places inside `.gm-style` via `map.controls[].push()`.
   * Worse, `.gm-style` is its own stacking context, so children cannot ever
   * exceed an outside sibling's explicit z-index. CSS-only fixes are
   * therefore impossible.
   *
   * Strategy
   * ────────
   * Move the deck.gl marker container INSIDE `.gm-style`, placed immediately
   * after the panes container. DOM order then resolves stacking among
   * siblings of `.gm-style` (all at z-index: auto):
   *
   *   1. basemap / panes (landmarks, geofences)   ← below
   *   2. deck.gl marker canvas                    ← unit markers
   *   3. control position containers (.mapButton) ← always on top
   *
   * Because Google appends every pushed control AFTER the panes container,
   * every control is guaranteed to be later in DOM order than deck.gl and
   * therefore paints on top — without any z-index gymnastics.
   *
   * Custom modals (`Overlay/index.js`, InfoWindow, cluster labels) are
   * unaffected: they are attached at `mapDiv` level with their own z-index
   * (200) so they continue to render above everything.
   */
  _raiseCanvasAboveMarkers() {
    const map = this._map;
    const mapDiv = map.getDiv();

    /**
     * Resolve the panes container (the direct child of `.gm-style` that
     * holds all 5 MapPanes). The official way to obtain a reliable
     * reference is to attach a throw-away OverlayView and read
     * `getPanes().floatPane.parentElement` once panes become available.
     */
    const resolvePanesContainer = (cb) => {
      if (this._panesContainer && this._panesContainer.isConnected) {
        cb(this._panesContainer);
        return;
      }
      const stub = new google.maps.OverlayView(); // eslint-disable-line no-undef
      stub.onAdd = () => {
        const panes = stub.getPanes?.();
        const container = panes?.floatPane?.parentElement || null;
        if (container) this._panesContainer = container;
        // Detach the stub now that we have the reference.
        try { stub.setMap(null); } catch (_) { /* noop */ }
        cb(this._panesContainer);
      };
      stub.draw = () => {};
      stub.setMap(map);
    };

    let attempts = 0;
    const MAX_ATTEMPTS = 60; // ~1s at 60 fps

    const place = () => {
      attempts++;
      // Find the deck.gl overlay that belongs to the marker layer.
      // Skip any overlay already tagged by the trail layer.
      const allOverlays = mapDiv.querySelectorAll('#deckgl-overlay');
      let canvas = null;
      for (let i = 0; i < allOverlays.length; i++) {
        const c = allOverlays[i];
        if (c.dataset.layerType === 'trail') continue;
        canvas = c;
        break;
      }
      if (!canvas || !canvas.parentElement) {
        if (attempts < MAX_ATTEMPTS) requestAnimationFrame(place);
        return;
      }

      canvas.dataset.layerType = 'markers';
      // Prevent bilinear filtering during CSS transforms applied by the
      // map at zoom/pan time.
      canvas.style.imageRendering = 'crisp-edges';
      canvas.style.imageRendering = '-webkit-optimize-contrast';

      const container = canvas.parentElement;
      // Inside `.gm-style` everything stacks via DOM order, so an explicit
      // z-index on the deck.gl container is no longer needed and would
      // actually re-introduce the original bug. Clear it.
      container.style.zIndex = '';
      container.style.pointerEvents = 'none';

      resolvePanesContainer((panesContainer) => {
        if (!panesContainer || !panesContainer.parentElement) {
          // Panes not ready yet — try again next frame.
          if (attempts < MAX_ATTEMPTS) requestAnimationFrame(place);
          return;
        }
        const gmStyle = panesContainer.parentElement;
        // Insert the deck.gl container as a direct child of `.gm-style`,
        // immediately after the panes container. Idempotent: only moves
        // when the position actually needs to change.
        const desiredNextSibling = panesContainer.nextSibling;
        if (container.parentElement !== gmStyle || container.nextSibling !== desiredNextSibling) {
          gmStyle.insertBefore(container, desiredNextSibling);
        }
      });
    };
    requestAnimationFrame(place);
  }

  /* ─── Internal: Layer Rendering ─── */

  /**
   * Filter overlapping labels using screen-space collision detection.
   * Projects each label to pixel coordinates, computes its bounding box,
   * and hides labels that overlap with a previously placed one.
   *
   * Uses a grid-based spatial index for O(n) average-case performance.
   * As the user zooms in, labels spread apart and more become visible.
   *
   * @param {Array} labelData  Items with _labelText and coordinates
   * @returns {Array}  Subset of labelData with non-overlapping labels
   */
  _filterOverlappingLabels(labelData) {
    if (!this._map || labelData.length === 0) return labelData;

    let projection, bounds;
    try {
      projection = this._map.getProjection();
      bounds = this._map.getBounds();
    } catch (e) {
      return labelData.length > MAX_VISIBLE_LABELS ? labelData.slice(0, MAX_VISIBLE_LABELS) : labelData;
    }
    if (!projection || !bounds) return labelData.length > MAX_VISIBLE_LABELS ? labelData.slice(0, MAX_VISIBLE_LABELS) : labelData;

    const zoom = this._map.getZoom();
    const scale = Math.pow(2, zoom);

    // Approximate label dimensions at 11px Outfit font
    const CHAR_WIDTH = 6.5;
    const LABEL_HEIGHT = 20;
    const OFFSET_X = 21;   // pixelOffset[0]
    const PAD_H = 12;      // backgroundPadding[0] * 2
    const CELL_SIZE = 60;  // grid cell in world-pixels

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    // Cache key: viewport bounds + zoom + data version.
    // If nothing moved, reuse the previous collision result instead
    // of re-running the O(n) grid check on every _renderLayers call.
    const cacheKey = `${Math.round(sw.lat() * 1e4)},${Math.round(sw.lng() * 1e4)},${Math.round(ne.lat() * 1e4)},${Math.round(ne.lng() * 1e4)},${Math.round(zoom * 100)},${this._dataVersion},${labelData.length}`;
    if (cacheKey === this._collisionCacheKey && this._collisionCacheResult) {
      return this._collisionCacheResult;
    }

    const swLng = sw.lng();
    const neLng = ne.lng();
    const crossesAntimeridian = swLng > neLng;
    const grid = new Map();
    const visible = [];

    for (let i = 0; i < labelData.length; i++) {
      const d = labelData[i];
      const [lng, lat] = d.coordinates;

      // Skip labels outside viewport
      if (lat < sw.lat() || lat > ne.lat()) continue;
      // Handle bounds that wrap across the antimeridian.
      // When the viewport crosses ±180°, Google Maps may return SW.lng() > NE.lng().
      const inLngRange = crossesAntimeridian
        ? (lng >= swLng || lng <= neLng)
        : (lng >= swLng && lng <= neLng);
      if (!inLngRange) continue;

      // Project to world-pixel coordinates
      const wp = projection.fromLatLngToPoint(
        new google.maps.LatLng(lat, lng) // eslint-disable-line no-undef
      );
      const px = wp.x * scale;
      const py = wp.y * scale;

      const tw = d._labelText.length * CHAR_WIDTH + PAD_H;
      const rect = {
        left: px + OFFSET_X,
        top: py - LABEL_HEIGHT / 2,
        right: px + OFFSET_X + tw,
        bottom: py + LABEL_HEIGHT / 2,
      };

      // Grid cells this rect touches
      const cx0 = Math.floor(rect.left / CELL_SIZE);
      const cx1 = Math.floor(rect.right / CELL_SIZE);
      const cy0 = Math.floor(rect.top / CELL_SIZE);
      const cy1 = Math.floor(rect.bottom / CELL_SIZE);

      let hit = false;
      outer: for (let cx = cx0; cx <= cx1; cx++) {
        for (let cy = cy0; cy <= cy1; cy++) {
          const key = `${cx},${cy}`;
          const cell = grid.get(key);
          if (cell) {
            for (let j = 0; j < cell.length; j++) {
              const o = cell[j];
              if (rect.left < o.right && rect.right > o.left &&
                  rect.top < o.bottom && rect.bottom > o.top) {
                hit = true;
                break outer;
              }
            }
          }
        }
      }

      if (!hit) {
        visible.push(d);
        for (let cx = cx0; cx <= cx1; cx++) {
          for (let cy = cy0; cy <= cy1; cy++) {
            const key = `${cx},${cy}`;
            let cell = grid.get(key);
            if (!cell) { cell = []; grid.set(key, cell); }
            cell.push(rect);
          }
        }
      }
    }

    // If everything got filtered out (common when bounds wrap or projection
    // behaves unexpectedly), fall back to a capped subset of the original data.
    // Cap prevents WebGL vertex buffer overflow on large accounts (3000+ units).
    let result;
    if (visible.length > 0) {
      result = visible.length > MAX_VISIBLE_LABELS ? visible.slice(0, MAX_VISIBLE_LABELS) : visible;
    } else {
      result = labelData.length > MAX_VISIBLE_LABELS ? labelData.slice(0, MAX_VISIBLE_LABELS) : labelData;
    }

    // Store in cache so repeated _renderLayers calls with the same
    // viewport + data skip the expensive grid collision detection.
    this._collisionCacheKey = cacheKey;
    this._collisionCacheResult = result;

    return result;
  }

  _getProjectionContext() {
    if (!this._map) return null;
    try {
      const projection = this._map.getProjection();
      const bounds = this._map.getBounds();
      const zoom = this._map.getZoom();
      if (!projection || !bounds || !Number.isFinite(zoom)) return null;
      const scale = Math.pow(2, zoom);
      return { projection, bounds, zoom, scale };
    } catch (e) {
      return null;
    }
  }

  _latLngToWorldPixels(lat, lng, projection, scale) {
    const wp = projection.fromLatLngToPoint(
      new google.maps.LatLng(lat, lng) // eslint-disable-line no-undef
    );
    return { x: wp.x * scale, y: wp.y * scale };
  }

  _worldPixelsToLngLat(x, y, projection, scale) {
    const pt = new google.maps.Point(x / scale, y / scale); // eslint-disable-line no-undef
    const ll = projection.fromPointToLatLng(pt);
    if (!ll) return null;
    return [ll.lng(), ll.lat()];
  }

  _rectIntersects(a, b) {
    return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
  }

  _gridKeysForRect(rect, cellSize) {
    const cx0 = Math.floor(rect.left / cellSize);
    const cx1 = Math.floor(rect.right / cellSize);
    const cy0 = Math.floor(rect.top / cellSize);
    const cy1 = Math.floor(rect.bottom / cellSize);
    const keys = [];
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let cy = cy0; cy <= cy1; cy++) {
        keys.push(`${cx},${cy}`);
      }
    }
    return keys;
  }

  _tryPlaceLabel({ anchorPx, anchorPy, width, height, viewportRect, grid, cellSize }) {
    // Candidates: progressively expanding rings around anchor.
    // We place labels using "start" anchor, meaning x is the LEFT edge.
    const MARKER_GAP = 26; // pixels away from marker center
    const MAX_RADIUS = 420;
    const STEP = 24;

    const pattern = [
      // right side (preferred)
      { side: 'right', dy: 0 },
      { side: 'right', dy: 22 },
      { side: 'right', dy: -22 },
      { side: 'right', dy: 44 },
      { side: 'right', dy: -44 },
      // top/bottom
      { side: 'top', dy: -30 },
      { side: 'bottom', dy: 30 },
      // left side
      { side: 'left', dy: 0 },
      { side: 'left', dy: 22 },
      { side: 'left', dy: -22 },
    ];

    const candidates = [];
    for (let r = 0; r <= MAX_RADIUS; r += STEP) {
      for (let i = 0; i < pattern.length; i++) {
        const p = pattern[i];
        let x;
        let y = anchorPy + p.dy + (r ? (p.dy >= 0 ? r : -r) * 0.15 : 0);
        if (p.side === 'right') {
          x = anchorPx + MARKER_GAP + r;
        } else if (p.side === 'left') {
          x = anchorPx - MARKER_GAP - r - width;
        } else {
          // centered above/below
          x = anchorPx - width / 2;
          y = anchorPy + (p.side === 'top' ? -(MARKER_GAP + r) : (MARKER_GAP + r));
        }
        candidates.push({ x, y });
      }
    }

    for (let i = 0; i < candidates.length; i++) {
      const { x, y } = candidates[i];
      const rect = {
        left: x,
        top: y - height / 2,
        right: x + width,
        bottom: y + height / 2,
      };

      // Must fit in viewport (small margin)
      if (
        rect.left < viewportRect.left + 4 ||
        rect.top < viewportRect.top + 4 ||
        rect.right > viewportRect.right - 4 ||
        rect.bottom > viewportRect.bottom - 4
      ) {
        continue;
      }

      // Collision check via grid
      const keys = this._gridKeysForRect(rect, cellSize);
      let hit = false;
      for (let k = 0; k < keys.length && !hit; k++) {
        const cell = grid.get(keys[k]);
        if (!cell) continue;
        for (let j = 0; j < cell.length; j++) {
          if (this._rectIntersects(rect, cell[j])) {
            hit = true;
            break;
          }
        }
      }
      if (hit) continue;

      // Commit occupancy
      for (let k = 0; k < keys.length; k++) {
        const key = keys[k];
        let cell = grid.get(key);
        if (!cell) {
          cell = [];
          grid.set(key, cell);
        }
        cell.push(rect);
      }
      return { x, y };
    }

    // Fallback: still place to the right even if it overlaps
    return { x: anchorPx + 26, y: anchorPy };
  }

  _layoutNoOverlapLabels(labelData) {
    const ctx = this._getProjectionContext();
    if (!ctx) return { labels: [], lines: [] };

    const { projection, bounds, scale } = ctx;
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    // Viewport in world pixels
    const nePx = this._latLngToWorldPixels(ne.lat(), ne.lng(), projection, scale);
    const swPx = this._latLngToWorldPixels(sw.lat(), sw.lng(), projection, scale);
    const viewportRect = {
      left: Math.min(nePx.x, swPx.x),
      right: Math.max(nePx.x, swPx.x),
      top: Math.min(nePx.y, swPx.y),
      bottom: Math.max(nePx.y, swPx.y),
    };

    // Approximate label sizes (Outfit 10px + padding)
    const CHAR_WIDTH = 6.5;
    const LINE_HEIGHT = 16;
    const PAD_H = 12;
    const PAD_V = 10;

    // Project visible points
    const projected = [];
    for (let i = 0; i < labelData.length; i++) {
      const d = labelData[i];
      const [lng, lat] = d.coordinates;
      if (lat < sw.lat() || lat > ne.lat() || lng < sw.lng() || lng > ne.lng()) continue;
      const px = this._latLngToWorldPixels(lat, lng, projection, scale);
      projected.push({ data: d, px: px.x, py: px.y });
    }
    if (projected.length === 0) return { labels: [], lines: [] };

    // Group by proximity (screen-space), MIN_GROUP_SIZE=5
    // Bigger radius -> more aggressive grouping into the 5+ DOM bubble.
    const PROX_R = 40;
    const PROX_SQ = PROX_R * PROX_R;
    const MIN_GROUP_SIZE = 5;

    const groups = [];
    for (let i = 0; i < projected.length; i++) {
      const p = projected[i];
      let bestIdx = -1;
      let bestDist = PROX_SQ;
      for (let g = 0; g < groups.length; g++) {
        const dx = p.px - groups[g].seedX;
        const dy = p.py - groups[g].seedY;
        const dist = dx * dx + dy * dy;
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = g;
        }
      }
      if (bestIdx >= 0) {
        groups[bestIdx].members.push(p);
      } else {
        groups.push({ seedX: p.px, seedY: p.py, members: [p] });
      }
    }

    const items = [];
    for (let g = 0; g < groups.length; g++) {
      const grp = groups[g];
      if (grp.members.length >= MIN_GROUP_SIZE) {
        const names = [];
        const unitIds = [];
        let sumLng = 0;
        let sumLat = 0;
        for (let m = 0; m < grp.members.length; m++) {
          const d = grp.members[m].data;
          names.push(d._labelText);
          unitIds.push(String(d.id));
          sumLng += d.coordinates[0];
          sumLat += d.coordinates[1];
        }

        names.sort((a, b) => a.localeCompare(b));
        const maxDisplay = 5;
        const display = names.slice(0, maxDisplay);
        const hasMore = names.length > maxDisplay;
        const namesText = display.join('\n');
        const fullLines = hasMore ? [...display, this._viewDetailText] : display;
        const fullText = fullLines.join('\n');
        const maxLen = fullLines.reduce((acc, s) => Math.max(acc, s.length), 0);

        items.push({
          kind: 'group',
          anchor: [sumLng / grp.members.length, sumLat / grp.members.length],
          anchorPx: grp.seedX,
          anchorPy: grp.seedY,
          text: fullText,
          fullText,
          namesText,
          displayNames: display,
          hasMore,
          lineCount: fullLines.length,
          linkOffsetY: hasMore ? ((fullLines.length - 1) / 2) * LINE_HEIGHT : 0,
          unitIds,
          count: grp.members.length,
          width: maxLen * CHAR_WIDTH + PAD_H,
          height: fullLines.length * LINE_HEIGHT + PAD_V,
        });
      } else {
        for (let m = 0; m < grp.members.length; m++) {
          const p = grp.members[m];
          const text = p.data._labelText;
          items.push({
            kind: 'single',
            id: p.data.id,
            anchor: p.data.coordinates,
            anchorPx: p.px,
            anchorPy: p.py,
            text,
            width: text.length * CHAR_WIDTH + PAD_H,
            height: LINE_HEIGHT + PAD_V,
          });
        }
      }
    }

    // Stable ordering helps avoid label jitter
    items.sort((a, b) => (a.anchorPy - b.anchorPy) || String(a.id || '').localeCompare(String(b.id || '')));

    const grid = new Map();
    const CELL_SIZE = 90;
    const placed = [];
    const lines = [];
    const circles = [];

    const toRadians = (deg) => (deg * Math.PI) / 180;
    const haversineMeters = (a, b) => {
      const R = 6371000;
      const [lng1, lat1] = a;
      const [lng2, lat2] = b;
      const dLat = toRadians(lat2 - lat1);
      const dLng = toRadians(lng2 - lng1);
      const rLat1 = toRadians(lat1);
      const rLat2 = toRadians(lat2);
      const sinDLat = Math.sin(dLat / 2);
      const sinDLng = Math.sin(dLng / 2);
      const h = sinDLat * sinDLat + Math.cos(rLat1) * Math.cos(rLat2) * sinDLng * sinDLng;
      return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
    };

    const makeCirclePath = (center, radiusMeters) => {
      const [lng, lat] = center;
      const latRad = toRadians(lat);
      const metersPerDegLat = 111320;
      const metersPerDegLng = metersPerDegLat * Math.max(0.1, Math.cos(latRad));
      const dLat = radiusMeters / metersPerDegLat;
      const dLng = radiusMeters / metersPerDegLng;

      const steps = 48;
      const pts = [];
      for (let i = 0; i <= steps; i++) {
        const t = (i / steps) * Math.PI * 2;
        pts.push([lng + Math.cos(t) * dLng, lat + Math.sin(t) * dLat]);
      }
      return pts;
    };

    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const placedPx = this._tryPlaceLabel({
        anchorPx: it.anchorPx,
        anchorPy: it.anchorPy,
        width: it.width,
        height: it.height,
        viewportRect,
        grid,
        cellSize: CELL_SIZE,
      });
      const lngLat = this._worldPixelsToLngLat(placedPx.x, placedPx.y, projection, scale);
      if (!lngLat) continue;

      placed.push({
        coordinates: lngLat,
        text: it.text,
        fullText: it.fullText,
        namesText: it.namesText,
        displayNames: it.displayNames,
        kind: it.kind,
        hasMore: it.hasMore,
        linkOffsetY: it.linkOffsetY,
        unitIds: it.unitIds,
        count: it.count,
      });

      // Leader line from anchor to label-left-center
      lines.push({ path: [it.anchor, lngLat], kind: it.kind });
    }

    // Build circles for 5+ groups using their member coordinates
    for (let g = 0; g < groups.length; g++) {
      const grp = groups[g];
      if (!grp.members || grp.members.length < MIN_GROUP_SIZE) continue;
      let sumLng = 0;
      let sumLat = 0;
      const coords = [];
      for (let m = 0; m < grp.members.length; m++) {
        const c = grp.members[m].data.coordinates;
        coords.push(c);
        sumLng += c[0];
        sumLat += c[1];
      }
      const center = [sumLng / grp.members.length, sumLat / grp.members.length];
      let maxR = 0;
      for (let m = 0; m < coords.length; m++) {
        maxR = Math.max(maxR, haversineMeters(center, coords[m]));
      }
      const radiusMeters = Math.max(30, maxR * 1.15);
      circles.push({ path: makeCirclePath(center, radiusMeters) });
    }

    return { labels: placed, lines, circles };
  }

  /**
   * Group nearby labels by screen-space proximity for the hybrid mode:
   * clusters OFF + "Label Clusters" ON.
   *
   * Groups of ≥ MIN_GROUP_SIZE are collected for DOM rendering (like cluster
   * labels), while smaller groups are emitted as individual labels and run
   * through the collision filter for the TextLayer.
   *
   * Uses greedy proximity-based clustering on projected marker positions.
   *
   * @param {Array} labelData  Items with _labelText and coordinates
   * @returns {{ singles: Array, groups: Array<{ center: [number,number], names: string[], unitData: Array, count: number }> }}
   */
  _groupOverlappingLabels(labelData) {
    const empty = { singles: [], groups: [] };
    if (!this._map || labelData.length === 0) return { singles: labelData, groups: [] };

    let projection, bounds;
    try {
      projection = this._map.getProjection();
      bounds = this._map.getBounds();
    } catch (e) {
      return { singles: labelData, groups: [] };
    }
    if (!projection || !bounds) return { singles: labelData, groups: [] };

    const zoom = this._map.getZoom();
    const scale = Math.pow(2, zoom);
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    // Proximity radius in world-pixels — labels within this distance
    // of an existing group seed are added to that group.
    const PROXIMITY_RADIUS = 100;
    const PROXIMITY_SQ = PROXIMITY_RADIUS * PROXIMITY_RADIUS;
    const MIN_GROUP_SIZE = 5;

    // Project all visible labels to screen coords
    const projected = [];
    for (let i = 0; i < labelData.length; i++) {
      const d = labelData[i];
      const [lng, lat] = d.coordinates;
      if (lat < sw.lat() || lat > ne.lat() || lng < sw.lng() || lng > ne.lng()) continue;

      const wp = projection.fromLatLngToPoint(
        new google.maps.LatLng(lat, lng) // eslint-disable-line no-undef
      );
      projected.push({ data: d, px: wp.x * scale, py: wp.y * scale });
    }

    // Greedy proximity grouping: assign each label to the nearest
    // group seed within PROXIMITY_RADIUS, or start a new group.
    // Group center is the seed point (first member) — does NOT drift.
    const groups = []; // { seedX, seedY, members: [] }

    for (let i = 0; i < projected.length; i++) {
      const p = projected[i];
      let bestIdx = -1;
      let bestDistSq = PROXIMITY_SQ;

      for (let g = 0; g < groups.length; g++) {
        const dx = p.px - groups[g].seedX;
        const dy = p.py - groups[g].seedY;
        const distSq = dx * dx + dy * dy;
        if (distSq < bestDistSq) {
          bestDistSq = distSq;
          bestIdx = g;
        }
      }

      if (bestIdx >= 0) {
        groups[bestIdx].members.push(p);
      } else {
        groups.push({ seedX: p.px, seedY: p.py, members: [p] });
      }
    }

    // Separate into groups (≥ MIN_GROUP_SIZE) and singles
    const singles = [];
    const labelGroups = [];

    for (let g = 0; g < groups.length; g++) {
      const grp = groups[g];
      if (grp.members.length >= MIN_GROUP_SIZE) {
        // Compute centroid for DOM overlay placement
        let sumLng = 0, sumLat = 0;
        const names = [];
        const unitData = [];
        for (let m = 0; m < grp.members.length; m++) {
          const d = grp.members[m].data;
          sumLng += d.coordinates[0];
          sumLat += d.coordinates[1];
          names.push(d._labelText);
          unitData.push(d);
        }
        labelGroups.push({
          center: [sumLng / grp.members.length, sumLat / grp.members.length],
          names,
          unitData,
          count: grp.members.length,
        });
      } else {
        // Small group — emit as individual labels
        for (let m = 0; m < grp.members.length; m++) {
          singles.push(grp.members[m].data);
        }
      }
    }

    // Apply collision filter on the singles so they don't overlap each other
    const filteredSingles = this._filterOverlappingLabels(singles);
    return { singles: filteredSingles, groups: labelGroups };
  }

  /**
   * Compute the display text for a label datum based on current settings.
   * @param {Object} d  Data item from _data
   * @returns {string}
   */
  _computeLabelText(d) {
    const { showUnit, showDriver } = this._labelSettings;
    const unit = String(d.label ?? '').trim();
    const driver = String(d.labelDriver ?? '').trim();
    if (showUnit && showDriver) {
      if (unit && driver) return `${unit} (${driver})`.substring(0, 40);
      return (unit || driver).substring(0, 40);
    }
    if (showUnit) return unit.substring(0, 40);
    if (showDriver) return driver.substring(0, 40);
    return '';
  }

  /**
   * Resolve the display angle for a marker using shortest-path rotation.
   * Persists the resolved value in _lastAngleMap so consecutive updates
   * always take the shortest arc (≤180°), preventing visible 360° spins.
   * @param {Object} d  Data item from _data
   * @returns {number}  Angle in degrees (negated heading for deck.gl convention)
   */
  _resolveAngle(d) {
    // Offline or static icons: no rotation
    if (!d.useRotation) return 0;

    const id = d.id;
    const rawHeading = Number(d.heading);
    // Guard: treat NaN / undefined / null heading as 0
    const heading = Number.isFinite(rawHeading) ? rawHeading : 0;
    const target = -heading;
    const last = this._lastAngleMap.get(id);

    if (last === undefined || !Number.isFinite(last)) {
      // First time seeing this unit (or stale NaN) — jump directly, no spin
      this._lastAngleMap.set(id, target);
      return target;
    }

    // If this marker just finished animating (in _fadingInIds), jump
    // directly to the target to avoid shortest-path interpolation
    // from a potentially stale cached angle.
    if (this._fadingInIds.has(id)) {
      this._lastAngleMap.set(id, target);
      return target;
    }

    // Shortest-path delta: 355°→5° becomes +10°, not −350°
    const delta = target - last;
    const shortest = ((delta % 360) + 540) % 360 - 180;
    const resolved = last + shortest;

    this._lastAngleMap.set(id, resolved);
    return resolved;
  }

  /**
   * Create an IconLayer for unit markers from a data array.
   * Shared between normal mode and clustered mode (unclustered singles).
   * @param {Array} data  Array of mapObjectToDeckData items
   * @param {string} layerId  Unique layer ID
   * @param {boolean} [animate=true]  Enable position/angle transitions.
   *        Set to false in clustered mode to prevent markers from "flying"
   *        across the map when cluster composition changes (different markers
   *        land in the same data slot and deck.gl would tween between the
   *        old and new position).
   * @returns {IconLayer}
   */
  /**
   * Build IconLayer(s) for unit markers.
   *
   * Returns an **array** of layers — callers must spread into their layers list.
   *
   * We split arrows (data-URI SVGs) and custom icons (HTTP PNGs) into two
   * independent IconLayers so each one manages its own icon atlas.  This
   * prevents deck.gl's async PNG fetching from invalidating the arrow atlas
   * and causing arrow markers to disappear while custom icons load.
   */
  _createMarkerIconLayers(data, layerId, animate = true) {
    const arrowData = [];
    const customData = [];
    for (let i = 0, len = data.length; i < len; i++) {
      (data[i].iconUrl ? customData : arrowData).push(data[i]);
    }

    const transitionProps = animate
      ? {
        // No getAngle transition — _resolveAngle already handles
        // shortest-path rotation.  Animating getAngle causes deck.gl to
        // restart its GPU interpolation for ALL 3090 markers every time
        // _headingVersion bumps (every 1-2 s on real GPS updates),
        // producing visible spin artifacts on the ~3067 unchanged markers.
        // Without the transition, markers snap to the resolved angle;
        // since real heading changes are only a few degrees per second,
        // the snap is imperceptible.
        getPosition: {
          type: 'interpolation',
          duration: 0,
          enter: value => value,
        },
      }
      : {};

    // Shared interaction callbacks and update triggers
    const sharedProps = {
      pickable: true,
      billboard: true,
      getId: d => String(d.id),
      getPosition: d => d.coordinates,
      sizeScale: 1,
      sizeUnits: 'pixels',
      sizeMinPixels: 20,
      sizeMaxPixels: 50,
      getAngle: d => this._resolveAngle(d),
      getColor: [255, 255, 255, 255],
      onClick: (info) => {
        if (this._onClick && info.object) {
          // Timestamp the click so landmark handlers can detect a recent
          // unit click and suppress their own popup.
          this._unitClickedAt = Date.now();
          this._onClick(info.object._mapObj, info);
        }
      },
      onHover: (info) => {
        if (info.object) {
          this._hasHoveredMarker = true;
          const label = info.object.label || info.object.id;
          if (label && this._tooltipEl) {
            this._tooltipEl.textContent = label;
            this._tooltipEl.style.display = 'block';
            this._tooltipEl.style.left = `${info.x + 12}px`;
            this._tooltipEl.style.top = `${info.y - 12}px`;
          }
          if (this._onHover) this._onHover(info.object._mapObj, info);
        } else {
          this._hasHoveredMarker = false;
          if (this._tooltipEl) this._tooltipEl.style.display = 'none';
          if (this._onHover) this._onHover(null, info);
        }
      },
      transitions: transitionProps,
      onIconError: (evt) => {
        console.warn('[WebGL] Icon load failed:', evt?.url || evt);
      },
      updateTriggers: {
        getIcon: this._dataVersion,
        getAngle: [this._dataVersion, this._headingVersion, this._positionVersion],
        getPosition: this._positionVersion,
      },
    };

    const layers = [];

    // ── Arrow layer (pre-built Canvas atlas — synchronous, no URL loading) ──
    // ALWAYS create the layer even with empty data. This keeps the
    // IconLayer instance alive across renders so deck.gl never destroys
    // its internal texture.  The pre-built Canvas atlas + iconMapping
    // means getIcon returns a string key that resolves instantly — no
    // async image decoding or atlas rebuilds on zoom/click.
    layers.push(new IconLayer({
      ...sharedProps,
      id: `${layerId}-arrows`,
      data: arrowData,
      iconAtlas: _arrowAtlasCanvas,
      iconMapping: _arrowAtlasMapping,
      getIcon: d => _getArrowAtlasKey(d.color, d.hasVideo),
      getSize: 40,
    }));

    // ── Custom icon layer (HTTP PNGs — async load, separate atlas) ──
    // Also always present for the same lifecycle stability reason.
    layers.push(new IconLayer({
      ...sharedProps,
      id: `${layerId}-custom`,
      data: customData,
      getIcon: d => ({
        url: d.iconUrl,
        width: 128,
        height: 128,
        anchorX: 64,
        anchorY: 64,
      }),
      // Offline icons should match the arrow size; other custom static icons
      // (trucks, circles, etc.) are designed at a smaller visual footprint.
      getSize: d => d.iconUrl === OFFLINE_ICON_URL ? 40 : 26,
    }));

    return layers;
  }

  /**
   * Build an IconLayer for route-stop pin markers.
   *
   * Route stops use a tall pin SVG (32×48 viewBox, rendered at 128×192)
   * with a status-coloured fill and sequence number.  They are never
   * clustered and never rotated.
   *
   * @param {Array}  data     Items from _data where isRouteStop === true
   * @param {string} layerId  Unique layer ID prefix
   * @returns {IconLayer}
   */
  /**
   * Dedicated IconLayer for route-stop pin markers.
   *
   * Uses a square icon (128×128 with viewBox 0 0 32 48) to replicate the
   * squished pin look of the original scaledSize(32,32) Google Maps marker.
   * Normal size = 32px, magnified (selected) = 48px — matching the DOM
   * marker behavior exactly.
   *
   * @param {Array} data     Route stop data items
   * @param {string} layerId  Unique layer ID
   * @returns {IconLayer}
   */
  _createRouteStopIconLayer(data, layerId) {
    const selectedId = this._selectedRouteStopId;

    const layer = new IconLayer({
      id: layerId,
      data,
      pickable: true,
      billboard: true,
      getPosition: d => d.coordinates,
      getIcon: d => ({
        // iconKey groups stops by visual appearance (color + number)
        // so the atlas reuses one entry for all stops that look alike.
        // Epoch is embedded in the LAYER ID (not here) to force a fresh
        // IconManager when route stops reappear after going empty.
        id: `rs-${d.iconKey}`,
        url: d.iconUrl,
        width: 512,
        height: 512,
        anchorX: 256,
        anchorY: 512,     // anchor at the pin tip (bottom-center)
      }),
      // Normal = 36px, selected = 52px
      getSize: d => d.id === selectedId ? 52 : 36,
      sizeScale: 1,
      sizeUnits: 'pixels',
      sizeMinPixels: 24,
      sizeMaxPixels: 64,
      getAngle: 0,       // route stops are never rotated
      getColor: [255, 255, 255, 255],
      onIconError: (evt) => {
        console.error('[WebGL] Route stop icon load error:', evt);
      },
      onClick: (info) => {
        if (info.object) {
          this._unitClickedAt = Date.now();
          // Route stop click callback takes priority
          if (this._onRouteStopClick) {
            this._onRouteStopClick(info.object._mapObj, info);
          } else if (this._onClick) {
            this._onClick(info.object._mapObj, info);
          }
        }
      },
      onHover: (info) => {
        if (info.object) {
          this._hasHoveredMarker = true;
          const label = info.object.label || info.object.id;
          if (label && this._tooltipEl) {
            this._tooltipEl.textContent = label;
            this._tooltipEl.style.display = 'block';
            this._tooltipEl.style.left = `${info.x + 12}px`;
            this._tooltipEl.style.top = `${info.y - 12}px`;
          }
          if (this._onHover) this._onHover(info.object._mapObj, info);
        } else {
          this._hasHoveredMarker = false;
          if (this._tooltipEl) this._tooltipEl.style.display = 'none';
          if (this._onHover) this._onHover(null, info);
        }
      },
      updateTriggers: {
        getIcon: this._dataVersion,
        getPosition: this._positionVersion,
        getSize: selectedId,
      },
    });

    return layer;
  }

  /**
   * Create a TextLayer for unit labels.
   * Uses deck.gl's built-in font atlas — all labels render in a single GPU
   * draw call with 0 DOM nodes and instant appearance (no SVG loading).
   *
   * Each label is positioned offset to the right of its marker (pixelOffset)
   * with a white background pill and subtle border.
   *
   * @param {Array} data  Array of mapObjectToDeckData items
   * @param {string} layerId  Unique layer ID
   * @returns {TextLayer|null}
   */
  _createLabelTextLayer(data, layerId) {
    if (!this._labelSettings.enabled || data.length === 0) return null;

    // Filter to items that have label text
    const labelData = [];
    for (let i = 0, len = data.length; i < len; i++) {
      const text = this._computeLabelText(data[i]);
      if (text) {
        labelData.push({ ...data[i], _labelText: text });
      }
    }
    if (labelData.length === 0) return null;

    // Determine visible labels for the TextLayer.
    // When label grouping is active (clusters OFF + "Label Clusters" ON),
    // nearby labels are grouped for DOM rendering and only the remaining
    // singles are shown in the TextLayer.
    let filteredData;
    if (this._labelGroupingEnabled) {
      const { singles, groups } = this._groupOverlappingLabels(labelData);
      this._labelGroups = groups;
      filteredData = singles;
    } else {
      this._labelGroups = [];
      // JS-based collision filter — hides overlapping labels in screen space.
      // CollisionFilterExtension is not compatible with GoogleMapsOverlay,
      // so we do it in JS before creating the layer.
      filteredData = this._filterOverlappingLabels(labelData);
    }
    if (filteredData.length === 0) return null;

    // Safety cap: prevent WebGL vertex buffer overflow on large accounts
    const cappedData = filteredData.length > MAX_VISIBLE_LABELS ? filteredData.slice(0, MAX_VISIBLE_LABELS) : filteredData;

    return new TextLayer({
      id: `${layerId}-f${this._fontAtlasVersion}`,
      data: cappedData,
      pickable: false,
      billboard: true,
      getId: d => String(d.id),
      getPosition: d => d.coordinates,
      getText: d => d._labelText,
      getSize: 12,
      sizeUnits: 'pixels',
      // Hide labels for markers currently being animated (trail animation only).
      // Labels whose animation just ended (in _fadingInIds) get full opacity so
      // the transition fades them in from 0.
      getColor: d => {
        if (this._animatingIds.has(String(d.id))) return [0, 0, 0, 0];
        return this._labelSettings.darkLabels ? [255, 255, 255, 255] : [0, 0, 0, 255];
      },
      // Roboto is a humanist sans-serif designed for screen legibility:
      // wider letter forms, taller descenders (clear 'j', 'g', 'y'), and
      // better glyph differentiation at small sizes than Outfit (geometric).
      // Already loaded via Google Fonts in index.html (weights 100-700).
      fontFamily: 'Roboto, Arial, sans-serif',
      fontWeight: 500,
      fontSettings: LABEL_FONT_SETTINGS,
      // Outline fattens glyph strokes via SDF, making thin characters (i, j)
      // more visible. Color matches text color for consistency.
      outlineWidth: 0.8,
      outlineColor: this._labelSettings.darkLabels ? [255, 255, 255, 255] : [0, 0, 0, 255],
      getTextAnchor: 'start',
      getAlignmentBaseline: 'center',
      // Offset label to the right of the marker (matches .popup-bubble left: 21px)
      getPixelOffset: [21, 0],
      // Dark pill background — darker than before, matching the hover tooltip
      // for maximum contrast and readability.
      background: true,
      getBackgroundColor: d => {
        if (this._animatingIds.has(String(d.id))) return [0, 0, 0, 0];
        return this._labelSettings.darkLabels ? [0, 0, 0, 200] : [255, 255, 255, 255];
      },
      backgroundPadding: [6, 3],
      getBorderColor: this._labelSettings.darkLabels ? [0, 0, 0, 0] : [200, 200, 200, 200],
      getBorderWidth: this._labelSettings.darkLabels ? 0 : 1,
      // Only apply fade-in enter transition when a trail animation just ended.
      // Without this guard, every label that enters the layer (e.g., from
      // collision filtering changes) would start transparent and flicker.
      transitions: this._fadingInIds.size > 0 ? {
        getColor: { duration: 400, enter: value => [value[0], value[1], value[2], 0] },
        getBackgroundColor: { duration: 400, enter: value => [value[0], value[1], value[2], 0] },
      } : {},
      updateTriggers: {
        getText: this._dataVersion,
        getColor: [this._dataVersion, this._animatingCount, this._fadingInIds.size, this._labelSettings.darkLabels],
        getBackgroundColor: [this._dataVersion, this._animatingCount, this._fadingInIds.size, this._labelSettings.darkLabels],
      },
    });
  }

  _createNoOverlapLabelLayers(data) {
    if (!this._labelSettings.enabled || data.length === 0) return { lineLayer: null, labelLayer: null };
    const zoom = this._map?.getZoom?.() ?? 0;
    if (!this._noOverlapLabels.enabled || zoom <= this._noOverlapLabels.minZoom) {
      this._noOverlapGroupLabels = [];
      return { lineLayer: null, labelLayer: this._createLabelTextLayer(data, 'webgl-unit-labels') };
    }

    // Build label data with computed text
    const labelData = [];
    for (let i = 0, len = data.length; i < len; i++) {
      const text = this._computeLabelText(data[i]);
      if (text) labelData.push({ ...data[i], _labelText: text });
    }
    if (labelData.length === 0) return { lineLayer: null, labelLayer: null };

    const { labels, lines, circles } = this._layoutNoOverlapLabels(labelData);
    if (labels.length === 0) {
      // Initial map load: projection/bounds may not be ready yet.
      // Fall back to the regular label layer so labels show immediately.
      this._noOverlapGroupLabels = [];
      return { lineLayer: null, labelLayer: this._createLabelTextLayer(data, 'webgl-unit-labels') };
    }

    // Extract 5+ groups (rendered via DOM MarkerOverlay for reliable "View Detail" UI)
    const groupLabels = labels.filter((d) => d.kind === 'group');
    this._noOverlapGroupLabels = groupLabels.map((d) => ({
      coordinates: d.coordinates,
      displayNames: Array.isArray(d.displayNames)
        ? d.displayNames
        : String(d.namesText || '').split('\n').filter(Boolean),
      unitIds: Array.isArray(d.unitIds) ? d.unitIds : [],
      count: d.count || (Array.isArray(d.unitIds) ? d.unitIds.length : 0),
      hasMore: Boolean(d.hasMore),
    }));

    // Only render non-group labels in WebGL to avoid duplicates under the DOM bubble
    const normalLabels = labels.filter((d) => d.kind !== 'group');

    // Lines are only needed for labels that visually move away from their marker.
    // For 5+ grouped labels we render a DOM bubble; its anchor/offset differs,
    // and the line tends to look misaligned. So we omit group leader lines.
    const normalLines = lines.filter((d) => d.kind !== 'group');

    const circleLayer = (circles && circles.length)
      ? new PathLayer({
        id: 'webgl-unit-label-group-circles',
        data: circles,
        pickable: false,
        getPath: d => d.path,
        // Use the same link blue already present in legacy styles (#0098DC)
        getColor: [0, 152, 220, 180],
        widthUnits: 'pixels',
        getWidth: 2,
      })
      : null;

    const lineLayer = normalLines.length
      ? new PathLayer({
        id: 'webgl-unit-label-leader-lines',
        data: normalLines,
        pickable: false,
        getPath: d => d.path,
        getColor: [176, 176, 176, 255],
        widthUnits: 'pixels',
        getWidth: 1,
      })
      : null;

    // Normal labels (singles + small groups): one layer with background and black text
    // Safety cap: prevent WebGL vertex buffer overflow on large accounts
    const cappedNormalLabels = normalLabels.length > MAX_VISIBLE_LABELS ? normalLabels.slice(0, MAX_VISIBLE_LABELS) : normalLabels;
    const labelLayer = cappedNormalLabels.length
      ? new TextLayer({
        id: `webgl-unit-labels-no-overlap-f${this._fontAtlasVersion}`,
        data: cappedNormalLabels,
        pickable: false,
        getPosition: d => d.coordinates,
        getText: d => d.text,
        getSize: 12,
        sizeUnits: 'pixels',
        getColor: d => {
          if (d.id != null && this._animatingIds.has(String(d.id))) return [0, 0, 0, 0];
          return this._labelSettings.darkLabels ? [255, 255, 255, 255] : [0, 0, 0, 255];
        },
        billboard: true,
        // Roboto: better small-size legibility than Outfit (geometric).
        fontFamily: 'Roboto, Arial, sans-serif',
        fontWeight: 500,
        fontSettings: LABEL_FONT_SETTINGS,
        outlineWidth: 0.8,
        outlineColor: this._labelSettings.darkLabels ? [255, 255, 255, 255] : [0, 0, 0, 255],
        getTextAnchor: 'start',
        getAlignmentBaseline: 'center',
        getPixelOffset: [0, 0],
        background: true,
        getBackgroundColor: d => {
          if (d.id != null && this._animatingIds.has(String(d.id))) return [0, 0, 0, 0];
          return this._labelSettings.darkLabels ? [0, 0, 0, 200] : [255, 255, 255, 255];
        },
        backgroundPadding: [6, 4],
        getBorderColor: this._labelSettings.darkLabels ? [0, 0, 0, 0] : [200, 200, 200, 200],
        getBorderWidth: this._labelSettings.darkLabels ? 0 : 1,
        // Only apply fade-in enter transition when a trail animation just ended.
        transitions: this._fadingInIds.size > 0 ? {
          getColor: { duration: 400, enter: value => [value[0], value[1], value[2], 0] },
          getBackgroundColor: { duration: 400, enter: value => [value[0], value[1], value[2], 0] },
        } : {},
        updateTriggers: {
          getColor: [this._dataVersion, this._animatingCount, this._fadingInIds.size, this._labelSettings.darkLabels],
          getBackgroundColor: [this._dataVersion, this._animatingCount, this._fadingInIds.size, this._labelSettings.darkLabels],
        },
      })
      : null;

    return { lineLayer, circleLayer, labelLayer };
  }

  /**
   * Lightweight render for position-only changes during marker animation.
   * Only rebuilds the IconLayer — labels and clusters are NOT recalculated,
   * avoiding expensive overlap logic and icon atlas regeneration that causes
   * visible flickering when the map is panning (Follow mode).
   *
   * Label TextLayer is rebuilt only every LABEL_THROTTLE_FRAMES position
   * updates (~4fps) instead of every animation frame (60fps). This avoids
   * running collision detection for all 10,000+ labels on every rAF tick
   * which can consume 5-20ms of frame budget and cause visible stutter.
   */
  _renderPositionUpdate() {
    if (!this._overlay || this._hidden) return;
    if (this._clusterEnabled) {
      // Clustering requires full rebuild — animation during clustering is rare
      this._scheduleRender();
      return;
    }

    const layers = [];

    // Split units and route stops for position-only update
    const unitData = [];
    const routeStopData = [];
    for (let i = 0, len = this._data.length; i < len; i++) {
      (this._data[i].isRouteStop ? routeStopData : unitData).push(this._data[i]);
    }

    // When _hideUnitsOnly is active, render ONLY route stop pins
    // (plus the pinned unit if one is set)
    if (this._hideUnitsOnly) {
      const pinnedData = this._pinnedUnitId
        ? unitData.filter(d => String(d.id) === this._pinnedUnitId)
        : [];
      layers.push(this._createRouteStopIconLayer(routeStopData, `webgl-route-stop-markers-e${this._routeStopEpoch}`));
      layers.push(...this._createMarkerIconLayers(pinnedData, 'webgl-unit-markers', false));
      if (pinnedData.length > 0) {
        const pinnedLabel = this._createLabelTextLayer(pinnedData, 'webgl-unit-labels');
        if (pinnedLabel) layers.push(pinnedLabel);
      }
      this._overlay.setProps({ layers });
      return;
    }

    // Position-only: animate=false to avoid transition fighting with rAF animation
    layers.push(...this._createMarkerIconLayers(unitData, 'webgl-unit-markers', false));

    // For labels: NEVER rebuild from scratch during rAF animation frames.
    // Rebuilding runs collision detection which produces unstable results
    // (different labels visible each call), causing visible flicker.
    // Instead, always reuse the label layers from the last full _renderLayers().
    // Labels will be properly updated when the animation ends and
    // scheduleWebGLRefresh triggers a full _renderLayers() call.
    if (this._animatingIds.size > 0 && this._cachedAnimationLabelLayer) {
      // Trail animation active — use the cached static label layer (excludes animating markers)
      layers.push(this._cachedAnimationLabelLayer);
    } else if (this._cachedLabelLayers) {
      const { labelLayer } = this._cachedLabelLayers;
      if (labelLayer) layers.push(labelLayer);
    }

    // Reuse cached line/circle overlays (they don't move during animation)
    if (this._cachedLabelLayers) {
      const { lineLayer, circleLayer } = this._cachedLabelLayers;
      if (lineLayer) layers.push(lineLayer);
      if (circleLayer) layers.push(circleLayer);
    }

    // Route stop layer (always present)
    layers.push(this._createRouteStopIconLayer(routeStopData, `webgl-route-stop-markers-e${this._routeStopEpoch}`));

    this._overlay.setProps({ layers });

    // Force repaint so the updated angles are visible immediately.
    // Without this, GoogleMapsOverlay may defer the paint until the next
    // Google Maps draw() cycle, leaving the old angle on screen.
    this._requestOverlayRedraw();
  }

  /* ─── Render Scheduling ─── */

  /**
   * Trigger a synchronous render and force deck.gl to repaint.
   *
   * All state mutations (setZoom, updateMarkers, setLabelSettings, etc.)
   * call this method.  The "always-present layers" design ensures deck.gl
   * never destroys / recreates IconLayer instances (which would reset the
   * async icon atlas), so multiple setProps calls within one frame are
   * safe — each call updates the existing layers in-place.
   *
   * beginUpdate / endUpdate batching is still available for callers that
   * need to group multiple mutations into a single render pass.
   *
   * @private
   */
  _scheduleRender() {
    this._renderLayers();
  }

  /**
   * No-op — retained for API compatibility with callers that may still
   * invoke it (setVisible, destroy).
   * @private
   */
  _cancelScheduledRender() {
    // Synchronous rendering — nothing to cancel.
  }

  /**
   * Force deck.gl to repaint its canvas immediately.
   *
   * GoogleMapsOverlay (interleaved: false) only paints the deck.gl canvas
   * inside Google Maps' `draw()` / `onDraw()` callback.  When we call
   * `overlay.setProps({ layers })` outside that cycle (e.g., in the `idle`
   * handler), the new layers are stored internally but the canvas is NOT
   * repainted until Google Maps happens to trigger another draw.
   *
   * This method bridges the gap by:
   *  1. Calling `deck.redraw()` on the internal Deck instance for an
   *     immediate synchronous paint of the current layers.
   *  2. Requesting Google Maps to schedule its next `onDraw` callback
   *     so the overlay's viewState stays synchronised with the map camera.
   *
   * Both calls access private GoogleMapsOverlay internals (`_deck` and
   * `_overlay`) — wrapped in try/catch for forward-compatibility.
   *
   * @private
   */
  _requestOverlayRedraw() {
    try {
      const deck = this._overlay._deck;
      if (deck) {
        deck.redraw('cluster layer update');
      }
    } catch (e) { /* best-effort */ }
    try {
      const innerOverlay = this._overlay._overlay;
      if (innerOverlay && typeof innerOverlay.requestRedraw === 'function') {
        innerOverlay.requestRedraw();
      }
    } catch (e) { /* best-effort */ }
  }

  /**
   * Adaptive icon atlas watchdog.
   *
   * Polls deck.gl's internal layer instances until all IconLayers report
   * `isLoaded === true` (meaning the async icon atlas is fully built and
   * the GPU texture has been uploaded).  On each tick where a layer is
   * still loading, forces a redraw so the freshly completed atlas is
   * painted immediately.
   *
   * Self-terminates after all layers are loaded or after MAX_ATTEMPTS
   * (prevents infinite loops if something is permanently broken).
   *
   * @private
   */
  _startIconWatchdog() {
    // Clear any existing watchdog — a new render cycle supersedes the old one
    if (this._iconWatchdogTimer) {
      clearTimeout(this._iconWatchdogTimer);
      this._iconWatchdogTimer = null;
    }
    if (this._data.length === 0) return;

    let attempts = 0;
    const MAX_ATTEMPTS = 40; // 40 × 50ms = 2 seconds max
    const INTERVAL = 50;     // ms between checks

    const check = () => {
      attempts++;
      try {
        const deck = this._overlay?._deck;
        if (!deck?.layerManager) {
          // Deck not yet initialized — retry
          if (attempts < MAX_ATTEMPTS) {
            this._iconWatchdogTimer = setTimeout(check, INTERVAL);
          }
          return;
        }

        const layers = deck.layerManager.layers || [];
        const arrowLayer = layers.find(l => l.id?.includes('arrows'));
        const customLayer = layers.find(l => l.id?.includes('custom'));

        const arrowLoaded = !arrowLayer || arrowLayer.isLoaded !== false;
        const customLoaded = !customLayer || customLayer.isLoaded !== false;

        if (!arrowLoaded || !customLoaded) {
          // Atlas still loading — force a redraw and retry
          this._requestOverlayRedraw();
          if (attempts < MAX_ATTEMPTS) {
            this._iconWatchdogTimer = setTimeout(check, INTERVAL);
          } else {
            // Timeout — one last redraw attempt
            this._requestOverlayRedraw();
            this._iconWatchdogTimer = null;
          }
          return;
        }

        // All layers loaded — final redraw to ensure paint is current
        this._requestOverlayRedraw();
        this._iconWatchdogTimer = null;
      } catch (e) {
        this._iconWatchdogTimer = null;
      }
    };

    // First check after 50ms (gives Image.onload time to fire for data URIs)
    this._iconWatchdogTimer = setTimeout(check, INTERVAL);
  }

  /**
   * Core render method — builds the complete layer array and pushes to deck.gl.
   *
   * DESIGN PRINCIPLES:
   * 1. ALL layer IDs are ALWAYS present in every setProps call (even with
   *    empty data arrays).  This prevents deck.gl from destroying/recreating
   *    IconLayer instances, which would reset the async icon atlas and cause
   *    invisible markers.
   * 2. Called synchronously via _scheduleRender().  The always-present layers
   *    design makes multiple setProps calls per frame safe.
   * 3. Post-render validation detects data/layer incoherence and self-heals.
   *
   * @private
   */
  _renderLayers() {
    if (!this._overlay) return;
    // Defer rendering while inside a beginUpdate / endUpdate batch
    if (this._batchDepth > 0) {
      this._batchNeedsRender = true;
      return;
    }
    // When fully hidden, keep layers empty so the overlay stays attached but invisible
    if (this._hidden) {
      this._overlay.setProps({ layers: [] });
      return;
    }
    const layers = [];

    // ── Split data: units vs route stops ──
    // Route stops are always rendered individually (never clustered)
    // and use a separate IconLayer with pin icons.
    const unitData = [];
    const routeStopData = [];
    for (let i = 0, len = this._data.length; i < len; i++) {
      (this._data[i].isRouteStop ? routeStopData : unitData).push(this._data[i]);
    }

    // When _hideUnitsOnly is active, skip all unit layers but still
    // render route-stop pins (plus the pinned unit if one is set).
    // This is used during route drawing to suppress fleet arrows
    // without hiding the route-stop markers or the driver's vehicle.
    if (this._hideUnitsOnly) {
      const pinnedData = this._pinnedUnitId
        ? unitData.filter(d => String(d.id) === this._pinnedUnitId)
        : [];
      layers.push(this._createRouteStopIconLayer(routeStopData, `webgl-route-stop-markers-e${this._routeStopEpoch}`));
      // Push pinned unit layer (or empty) to keep deck.gl instance alive (atlas preservation)
      layers.push(...this._createMarkerIconLayers(pinnedData, 'webgl-unit-markers', false));
      if (pinnedData.length > 0) {
        const pinnedLabel = this._createLabelTextLayer(pinnedData, 'webgl-unit-labels');
        if (pinnedLabel) layers.push(pinnedLabel);
      }
      this._overlay.setProps({ layers });
      this._requestOverlayRedraw();

      return;
    }

    // Suppress transitions when rAF animations are running — the animation
    // loop drives position updates at 60fps so deck.gl transitions would
    // fight with it and cause visible jitter.
    const animateMarkers = !this._suppressTransitionsOnce && this._animatingCount === 0;

    if (this._clusterEnabled) {
      // ── Clustered mode ──
      // Safety: if _clusterFeatures is empty but we have unit data, rebuild index.
      // This recovers from any state where the supercluster got out of sync.
      if (this._clusterFeatures.length === 0 && unitData.length > 0 && this._supercluster) {
        this._loadClusterIndex();
      }

      const singles = [];
      const clusters = [];
      for (let i = 0, len = this._clusterFeatures.length; i < len; i++) {
        const feature = this._clusterFeatures[i];
        if (feature.properties.cluster) {
          clusters.push(feature);
        } else {
          singles.push(feature.properties);
        }
      }

      // 1. Individual marker IconLayers — ALWAYS present (even with empty data).
      //    deck.gl updates the existing layer instance in-place when data
      //    changes from [] → [...items], preserving the icon atlas.
      //    animate=false: prevents markers from "flying" across the map when
      //    cluster composition changes on zoom in/out.
      layers.push(...this._createMarkerIconLayers(singles, 'webgl-unit-markers', false));

      // 2. Cluster icons — ALWAYS present (pre-built Canvas atlas)
      //    Uses the same pre-rendered atlas as arrows: getIcon returns a
      //    string key, iconMapping is static — no URL loading, no atlas
      //    rebuild.  Cluster icons never disappear during zoom/click.
      //
      //    IMPORTANT: `clusterPosAccessor` is shared with the TextLayer
      //    (step 3) so the count number and the icon always use the exact
      //    same coordinates, even during rapid zoom recalculation.
      const clusterPosAccessor = d => d.geometry.coordinates;
      const clusterIconLayer = new IconLayer({
        id: 'webgl-cluster-icons',
        data: clusters,
        pickable: true,
        billboard: true,
        iconAtlas: _clusterAtlasCanvas,
        iconMapping: _clusterAtlasMapping,
        getPosition: clusterPosAccessor,
        getIcon: d => {
          const count = d.properties.point_count;
          const color = getClusterColor(count);
          return `cluster-${color}`;
        },
        getSize: 50,
        sizeUnits: 'pixels',
        onClick: (info) => {
          if (info.object && this._map && this._supercluster) {
            this._unitClickedAt = Date.now();
            try {
              const cid = info.object.properties.cluster_id;
              const expansionZoom = this._supercluster.getClusterExpansionZoom(cid);
              const [lng, lat] = info.object.geometry.coordinates;
              // Mark transition BEFORE camera moves — see handleClick comment
              this._clusterTransitionPending = true;
              this._map.setZoom(Math.min(expansionZoom, 20));
              this._map.panTo({ lat, lng });
              this._scheduleDeferredClusterRecompute();
            } catch (e) {
              // Cluster may no longer exist after data update
              this._clusterTransitionPending = false;
            }
            return true;
          }
        },
        onHover: (info) => {
          if (info.object && this._tooltipEl) {
            this._tooltipEl.textContent = `${info.object.properties.point_count} units`;
            this._tooltipEl.style.display = 'block';
            this._tooltipEl.style.left = `${info.x + 12}px`;
            this._tooltipEl.style.top = `${info.y - 12}px`;
          } else if (this._tooltipEl) {
            this._tooltipEl.style.display = 'none';
          }
        },
        updateTriggers: {
          getIcon: [this._dataVersion, this._clusterVersion],
          getPosition: this._clusterVersion,
        },
      });
      layers.push(clusterIconLayer);

      // 3. Cluster count text — ALWAYS present
      //    Shares the SAME `clusters` data array and position accessor as
      //    the cluster IconLayer above.  Both layers receive identical
      //    coordinates in the same setProps() call, so the text never
      //    drifts from the icon even during rapid zoom changes.
      const clusterTextLayer = new TextLayer({
        id: `webgl-cluster-text-f${this._fontAtlasVersion}`,
        data: clusters,
        pickable: false,
        getPosition: clusterPosAccessor,
        getText: d => String(d.properties.point_count),
        getSize: 15,
        sizeUnits: 'pixels',
        getColor: [255, 255, 255, 255],
        // Bold weight for cluster counts
        fontFamily: 'Roboto, Arial, sans-serif',
        fontWeight: 700,
        fontSettings: LABEL_FONT_SETTINGS,
        outlineWidth: 0.8,
        outlineColor: [255, 255, 255, 255],
        getTextAnchor: 'middle',
        getAlignmentBaseline: 'center',
        billboard: true,
        updateTriggers: {
          getText: [this._dataVersion, this._clusterVersion],
          getPosition: this._clusterVersion,
        },
      });
      layers.push(clusterTextLayer);

      // 4. Labels for unclustered markers (TextLayer — pure WebGL)
      const labelLayer = this._createLabelTextLayer(singles, 'webgl-unit-labels');
      if (labelLayer) layers.push(labelLayer);

    } else {
      // ── Normal (non-clustered) mode ──
      // Only feed unit markers to the arrow/custom icon layers and labels.
      // Route stops are rendered via their own dedicated layer below.
      layers.push(...this._createMarkerIconLayers(unitData, 'webgl-unit-markers', animateMarkers));

      const labelResult = this._createNoOverlapLabelLayers(unitData);
      this._cachedLabelLayers = labelResult;
      const { lineLayer, circleLayer, labelLayer } = labelResult;
      if (lineLayer) layers.push(lineLayer);
      if (circleLayer) layers.push(circleLayer);
      if (labelLayer) layers.push(labelLayer);

      // Pre-build a label layer excluding animating markers for use by
      // _renderPositionUpdate during trail animations.
      if (this._animatingIds.size > 0) {
        const staticData = unitData.filter(d => !this._animatingIds.has(String(d.id)));
        this._cachedAnimationLabelLayer = staticData.length > 0
          ? this._createLabelTextLayer(staticData, 'webgl-unit-labels-static')
          : null;
      } else {
        this._cachedAnimationLabelLayer = null;
      }
    }

    // ── Route Stop layer — always rendered outside clustering ──
    // Layer ID includes the epoch so that on each 0→N route stop transition,
    // deck.gl creates a brand-new IconLayer with a fresh IconManager.
    // This prevents stale atlas textures from causing invisible markers.
    layers.push(this._createRouteStopIconLayer(routeStopData, `webgl-route-stop-markers-e${this._routeStopEpoch}`));

    this._overlay.setProps({ layers });

    // Force deck.gl to repaint — GoogleMapsOverlay (non-interleaved) does
    // not automatically repaint after setProps outside the draw() cycle.
    this._requestOverlayRedraw();

    // Adaptive icon atlas watchdog: polls deck.gl's internal layer state
    // until all IconLayers report isLoaded === true (meaning the async
    // icon atlas has been fully built and uploaded to GPU).  Replaces the
    // old fixed 50ms/200ms deferred redraws which were unreliable under
    // CPU load or when rapid updateMarkers calls reset the atlas.
    if (this._data.length > 0) {
      this._startIconWatchdog();
    }

    // Clear one-shot suppression after we successfully push layers.
    if (this._suppressTransitionsOnce) this._suppressTransitionsOnce = false;

    // Track rendered count for data/GPU coherence validation in invalidate().
    this._lastRenderedDataCount = this._data.length;

    // ── Post-render coherence check ──
    // Validates that the layers we pushed actually contain the expected
    // marker data.  Several desync scenarios can cause empty layers:
    //  - Stale _clusterFeatures after rapid zoom (getBounds was wrong)
    //  - enableClustering called before updateMarkers loaded _data
    //  - Supercluster index corrupted by 0,0 coordinates
    //
    // Check: in clustered mode, the sum of (singles + cluster leaves)
    // should approximate _data.length.  If the singles layer has 0 data
    // and no clusters exist either, the user sees an empty map.
    if (this._clusterEnabled && this._data.length > 0) {
      // Count how many individual markers were pushed to the arrow layer
      const arrowLayer = layers.find(l => l.id === 'webgl-unit-markers-arrows');
      const arrowCount = arrowLayer?.props?.data?.length ?? 0;
      const clusterLayer = layers.find(l => l.id === 'webgl-cluster-icons');
      const clusterCount = clusterLayer?.props?.data?.length ?? 0;

      // If we have data but NOTHING is rendering (0 singles, 0 clusters),
      // the supercluster index is out of sync.  Force a full rebuild.
      if (arrowCount === 0 && clusterCount === 0) {
        this._loadClusterIndex();
        // Use setTimeout(0) to avoid synchronous recursion — the current
        // call stack completes, then the recovery render fires.
        setTimeout(() => this._scheduleRender(), 0);
      }
    }
  }

  /* ─── Diagnostics ─── */

  /**
   * Dump the internal deck.gl data for a specific marker.
   * Call from the browser console to diagnose invisible markers:
   *   webglLayer.debugMarker('200075732')
   *
   * @param {string|number} id  Unit ID to inspect
   */
  debugMarker(id) {
    const entry = this._data.find(d => String(d.id) === String(id));
    if (!entry) {
      console.warn(`[WebGL debugMarker] id=${id} NOT found in _data (${this._data.length} items).`);
      console.warn('  The unit was filtered out by updateWebGLMarkers (enable/hideMarker/visible).');
      return null;
    }
    const iconSpec = entry.iconUrl
      ? { url: entry.iconUrl, width: 128, height: 128, layer: 'custom' }
      : { url: createColoredArrowUrl(entry.color), width: 128, height: 128, layer: 'arrows' };
    const labelText = this._computeLabelText(entry);
    console.group(`[WebGL debugMarker] id=${id}`);
    console.log('  coordinates:', entry.coordinates);
    console.log('  color:', entry.color);
    console.log('  iconUrl:', entry.iconUrl);
    console.log('  useRotation:', entry.useRotation);
    console.log('  heading:', entry.heading);
    console.log('  visible:', entry.visible);
    console.log('  label:', entry.label);
    console.log('  labelDriver:', entry.labelDriver);
    console.log('  computedLabelText:', labelText);
    console.log('  iconSpec:', iconSpec);
    console.log('  isRouteStop:', entry.isRouteStop);
    console.log('  _mapObj.offline:', entry._mapObj?.offline);
    console.log('  _mapObj.icon:', entry._mapObj?.icon);
    console.log('  _mapObj.enable:', entry._mapObj?.enable);
    console.log('  _mapObj.hideMarker:', entry._mapObj?.hideMarker);
    console.log('  _mapObj.labelDriver:', entry._mapObj?.labelDriver);
    console.log('  _mapObj.layerName:', entry._mapObj?.layerName);
    console.log('  _mapObj.value:', entry._mapObj?.value);
    console.log('  _mapObj.isRouteTracker:', entry._mapObj?.isRouteTracker);
    console.log('  _mapObj.eventName:', entry._mapObj?.eventName);
    console.groupEnd();
    return entry;
  }

  /**
   * Dump label rendering diagnostics.
   * Call from the browser console:
   *   webglLayer.debugLabels()
   *
   * Shows label settings, data summary, and sample entries to help
   * diagnose why labels aren't displaying correctly.
   */
  debugLabels() {
    const { enabled, showUnit, showDriver } = this._labelSettings;
    const totalData = this._data.length;

    // Count entries with label / labelDriver populated
    let withLabel = 0;
    let withDriver = 0;
    let withBoth = 0;
    let emptyText = 0;
    const sampleEntries = [];

    for (let i = 0, len = this._data.length; i < len; i++) {
      const d = this._data[i];
      const hasLabel = Boolean(String(d.label ?? '').trim());
      const hasDriver = Boolean(String(d.labelDriver ?? '').trim());
      if (hasLabel) withLabel++;
      if (hasDriver) withDriver++;
      if (hasLabel && hasDriver) withBoth++;

      const text = this._computeLabelText(d);
      if (!text) emptyText++;
      if (sampleEntries.length < 5) {
        sampleEntries.push({
          id: d.id,
          label: d.label,
          labelDriver: d.labelDriver,
          computedText: text,
        });
      }
    }

    console.group('[WebGL debugLabels]');
    console.log('  _labelSettings:', { enabled, showUnit, showDriver });
    console.log('  _noOverlapLabels:', this._noOverlapLabels);
    console.log('  _hidden:', this._hidden);
    console.log('  _clusterEnabled:', this._clusterEnabled);
    console.log('  _dataVersion:', this._dataVersion);
    console.log('  total markers in _data:', totalData);
    console.log('  markers with label (unit name):', withLabel);
    console.log('  markers with labelDriver:', withDriver);
    console.log('  markers with both:', withBoth);
    console.log('  markers with empty computed text:', emptyText);
    console.log('  would render labels:', totalData - emptyText);
    console.log('  sample entries:', sampleEntries);
    console.groupEnd();

    return {
      settings: { enabled, showUnit, showDriver },
      totalData,
      withLabel,
      withDriver,
      withBoth,
      emptyText,
      visibleLabels: totalData - emptyText,
    };
  }
}

export { WebGLMarkerLayer, mapObjectToDeckData };
